import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import MentionTextarea from './MentionTextarea';
import { removerAnexoDaTarefa } from '../../src/lib/anexosTarefa';
import { ComentarioTexto, corDoUsuario } from '../../src/components/comentarios/ComentarioTexto';
import { interpretarBusca, textoContem, textoCitaUc, numeroUcNormalizado, regexNumeroUc } from './buscaSolicitacao';
import {
  CheckSquare,
  List,
  KanbanIcon,
  Table as TableIcon,
  Plus,
  Clock,
  AlertCircle,
  MoreVertical,
  Flag,
  Trash2,
  ArrowUpDown,
  User,
  Calendar,
  X,
  Save,
  MessageSquare,
  Paperclip,
  Trash,
  Loader2,
  UserPlus,
  CalendarDays,
  Repeat,
  Send,
  Briefcase,
  SlidersHorizontal,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Search,
  BarChart3,
  Edit2,
  Check,
  Eye,
  EyeOff,
  Zap,
  Target,
  SquareChartGantt,
  Play,
  Pause,
  Square,
  ListTree,
  ClipboardList,
  Archive,
  GitBranch,
  MapPin,
  CornerDownRight
} from 'lucide-react';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { api } from '../../src/services/api';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import { supabase } from '../../src/lib/supabase';
import { logAction, logTarefaStatusEvent, logUsageEvent } from '../../src/lib/logger';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import { comentarioApagado, ocultarSeApagado, TEXTO_COMENTARIO_APAGADO } from '../../src/lib/comentarioApagado';
import {
  SOLICITACAO_ATUALIZADA_EVENTO, SOLICITACAO_CRIADA_EVENTO, SOLICITACAO_EXCLUIDA_EVENTO,
} from './useSolicitacaoActions';
import { formatDate, parseLocalDate } from '../../src/lib/utils';
import { criarChecagemTarefaArquivada } from '../../src/lib/tarefaArquivada';
import { Profile, Tarefa, ComentarioTarefa, TarefaPrioridade, TarefaStatus, TarefaCategoria, SolicitacaoStatus, SolicitacaoTipo, SolicitacaoDepartamento, TarefaTimeEntry, TarefaStatusEvent, Projeto } from '../../types';
import TaskModal from './TaskModal';
import { PrazoBadge, SeloUrgencia, ehUrgenciaAberta, CLASSE_URGENCIA } from './PrazoSolicitacao';
import { tipoSolicitacaoInfo, DEPARTAMENTO_BADGE_CLASS, isTipoOutro, EQUIPES_SOLICITACAO, equipeDoDepartamento, equipesDoPerfil, solicitacaoVisivel, veTodasAsSolicitacoes, etapasDoDepartamento, rotuloEtapa } from './solicitacaoTipos';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import TasksFilterModal from './TasksFilterModal';
import ModalTasksGraphic from './ModalTasksGraphic';
import TarefasKPIDashboard from './TarefasKPIDashboard';
import SolicitacoesGantt from './SolicitacoesGantt';
import SolicitacoesAuditDashboard from './SolicitacoesAuditDashboard';
import SeloSubtarefa, { SUBTAREFA_FAIXA } from './SeloSubtarefa';
import { agruparSubtarefas } from './subtarefasAgrupadas';
import { podeDirecionarSolicitacao, podeTrocarResponsavel, daEquipeDaSolicitacao, podeEntrarNaCentral, podeReceberPedido } from './permissoesSolicitacao';
import DatePicker from '../../src/components/DatePicker';
import TaskDescriptionMarkdown from './TaskDescriptionMarkdown';
import ResponsibleFilter, { SEM_RESPONSAVEL } from '../../src/components/ResponsibleFilter';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { normalizarModalidade } from '../../src/lib/modalidade';
import VisitaModal, { VisitaParadaInfo, VisitaConclusao } from '../CRM/Rotas/VisitaModal';
import NegotiationDetailModal from '../CRM/components/NegotiationDetails/NegotiationDetailModal';
import { concluirVisitaTarefa } from '../../src/lib/visitaTarefa';
import { SeloVisualizado } from './SeloVisualizado';
import { nomeEtapaNaTela } from '../../src/lib/nomeEtapaNaTela';

const getInitialState = <T,>(key: string, defaultValue: T): T => {
  try {
    const saved = localStorage.getItem(`tarefas_${key}`);
    return saved !== null ? JSON.parse(saved) : defaultValue;
  } catch {
    return defaultValue;
  }
};

type SortOrder = 'priority' | 'date' | 'concluidas';

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'priority', label: 'Prioridade' },
  { value: 'date', label: 'Data de Vencimento' },
  { value: 'concluidas', label: 'Últimas concluídas' },
];

const SORT_LABELS: Record<SortOrder, string> = {
  priority: 'Prioridade',
  date: 'Data de Vencimento',
  concluidas: 'Últimas concluídas',
};

// Batida do cronômetro de Solicitação. 60s contra a janela de silêncio de 30 min do
// encerrar_cronometros_esquecidos() — 30 batidas perdidas antes de ser considerado ausente.
const HEARTBEAT_INTERVAL_MS = 60_000;

interface TarefasViewProps {
  fixedCategoria?: TarefaCategoria;
}

const TarefasView: React.FC<TarefasViewProps> = ({ fixedCategoria }) => {
  const { user, profile } = useAuth();
  const { addToast } = useToast();
  const categoriaFilter: TarefaCategoria = fixedCategoria === 'solicitacao' ? 'solicitacao' : 'tarefa';
  // Mesma tela (TarefasView) atende /tarefas e /tarefas/solicitacoes por
  // prop -- usa a mesma distinção pra telemetria de uso.
  const usoFuncionalidade = categoriaFilter === 'solicitacao' ? 'solicitacoes' : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlView = searchParams.get('view') as 'list' | 'kanban' | 'table' | 'calendar' | 'gantt';
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'table' | 'calendar' | 'gantt'>(
    urlView || getInitialState(
      categoriaFilter === 'solicitacao' ? 'viewModeSolicitacao' : 'viewMode',
      categoriaFilter === 'solicitacao' ? 'table' : 'kanban'
    )
  );
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  // Projetos (módulo Projetos): alimentam o seletor "Projeto" do detalhe da Solicitação. Lista
  // curta, carregada uma vez — serve também pra exibir o nome em modo leitura.
  const [projetos, setProjetos] = useState<Projeto[]>([]);
  const podeVincularProjeto = profile?.role === 'adm' || profile?.role === 'gerente';
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState<SortOrder>(getInitialState('sortOrder', 'priority'));
  const [showCompletedTasks, setShowCompletedTasks] = useState(getInitialState('showCompletedTasks', false));
  const [showParceiros, setShowParceiros] = useState(getInitialState('showParceiros', true));
  // Visitas comerciais viram tarefa (tipo preenchido) e enchem o board de quem gerencia rota — toggle pra tirar da vista
  const [showVisitas, setShowVisitas] = useState(getInitialState('showVisitas', true));
  // Subtarefas ficam escondidas das visões Lista/Tabela/Kanban por padrão (senão o board fica poluído com filhas ao lado das mães) — Gantt sempre mostra
  const [showSubtasks, setShowSubtasks] = useState(getInitialState('showSubtasks', false));
  const [showBacklogOnly, setShowBacklogOnly] = useState(false);
  // Aba de equipe da Central (#6518) — substituiu a faixa "Carga de trabalho". Sem "Todas": abre na
  // equipe do departamento principal e lembra a última aba escolhida (chave por usuário: uma chave
  // global vazaria entre contas no mesmo navegador).
  const equipePrincipal = (equipesDoPerfil({ departamento: profile?.departamento })[0]
    || equipesDoPerfil(profile)[0] || EQUIPES_SOLICITACAO[0]).id;
  const chaveEquipe = user?.id ? `central_equipe_${user.id}` : null;
  const [equipeEscolhida, setEquipeEscolhida] = useState<string | null>(null);
  useEffect(() => {
    if (!chaveEquipe) return;
    let salva: string | null = null;
    try { salva = localStorage.getItem(chaveEquipe); } catch { /* navegador sem storage: usa a principal */ }
    setEquipeEscolhida(EQUIPES_SOLICITACAO.some(e => e.id === salva) ? salva : null);
  }, [chaveEquipe]);
  const equipeFiltro = equipeEscolhida || equipePrincipal;
  const setEquipeFiltro = (id: string) => {
    setEquipeEscolhida(id);
    if (chaveEquipe) { try { localStorage.setItem(chaveEquipe, id); } catch { /* ignora */ } }
  };

  // New Complex Filter State
  const [showFilterModal, setShowFilterModal] = useState(false);
  // Pesquisa da Central (#6570/#6571): título, descrição e comentários (a conclusão é um
  // comentário). Os comentários vêm do banco; "UC 123" procura pelo número da UC.
  const [buscaTexto, setBuscaTexto] = useState('');
  const [buscaDigitada, setBuscaDigitada] = useState('');
  const [buscaComentarios, setBuscaComentarios] = useState<Set<string>>(new Set());
  const [buscaUcNumeros, setBuscaUcNumeros] = useState<string[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showKPIDashboard, setShowKPIDashboard] = useState(false);
  const [showAuditDashboard, setShowAuditDashboard] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const initialComplexFilters = getInitialState('complexFilters', {
    status: [] as string[],
    titulo: '',
    prioridade: 'all',
    responsavelId: [] as string[],
    solicitanteId: [] as string[],
    vencimentoInicio: '',
    vencimentoFim: '',
    tipoSolicitacao: [] as string[],
    projetoId: [] as string[]
  });

  // Backward compatibility with older localStorage structure
  if (typeof initialComplexFilters.status === 'string') {
    initialComplexFilters.status = initialComplexFilters.status === 'all' ? [] : [initialComplexFilters.status];
  }
  if (typeof initialComplexFilters.responsavelId === 'string') {
    initialComplexFilters.responsavelId = initialComplexFilters.responsavelId === 'all' ? [] : [initialComplexFilters.responsavelId];
  }
  // getInitialState devolve o localStorage cru (sem merge com o default), então filtros salvos
  // antes do filtro por tipo voltam sem a chave e quebrariam o .includes/.length abaixo.
  if (!Array.isArray(initialComplexFilters.tipoSolicitacao)) {
    initialComplexFilters.tipoSolicitacao = [];
  }
  // Mesmo caso do tipoSolicitacao: filtros salvos antes do "Aberto por" voltam sem a chave.
  if (!Array.isArray(initialComplexFilters.solicitanteId)) {
    initialComplexFilters.solicitanteId = [];
  }
  if (!Array.isArray(initialComplexFilters.projetoId)) {
    initialComplexFilters.projetoId = [];
  }

  const [complexFilters, setComplexFilters] = useState<{
    status: string[];
    titulo: string;
    prioridade: string;
    responsavelId: string[];
    solicitanteId: string[];
    vencimentoInicio: string;
    vencimentoFim: string;
    tipoSolicitacao: string[];
    projetoId: string[];
  }>(initialComplexFilters);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [showCalendarViewDropdown, setShowCalendarViewDropdown] = useState(false);

  const handleNext = () => {
    const newDate = new Date(currentDate);
    switch (calendarView) {
      case 'day':
        newDate.setDate(currentDate.getDate() + 1);
        break;
      case 'week':
        newDate.setDate(currentDate.getDate() + 7);
        break;
      case 'month':
        newDate.setMonth(currentDate.getMonth() + 1);
        break;
      case 'year':
        newDate.setFullYear(currentDate.getFullYear() + 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    switch (calendarView) {
      case 'day':
        newDate.setDate(currentDate.getDate() - 1);
        break;
      case 'week':
        newDate.setDate(currentDate.getDate() - 7);
        break;
      case 'month':
        newDate.setMonth(currentDate.getMonth() - 1);
        break;
      case 'year':
        newDate.setFullYear(currentDate.getFullYear() - 1);
        break;
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // UI States for localized dropdowns
  const [showResponsibleDropdown, setShowResponsibleDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [selectedResponsibles, setSelectedResponsibles] = useState<string[]>(getInitialState('selectedResponsibles', []));

  useEffect(() => {
    localStorage.setItem(categoriaFilter === 'solicitacao' ? 'tarefas_viewModeSolicitacao' : 'tarefas_viewMode', JSON.stringify(viewMode));
    localStorage.setItem('tarefas_sortOrder', JSON.stringify(sortOrder));
    localStorage.setItem('tarefas_showCompletedTasks', JSON.stringify(showCompletedTasks));
    localStorage.setItem('tarefas_showParceiros', JSON.stringify(showParceiros));
    localStorage.setItem('tarefas_showVisitas', JSON.stringify(showVisitas));
    localStorage.setItem('tarefas_complexFilters', JSON.stringify(complexFilters));
    localStorage.setItem('tarefas_selectedResponsibles', JSON.stringify(selectedResponsibles));
  }, [viewMode, sortOrder, showCompletedTasks, showParceiros, showVisitas, complexFilters, selectedResponsibles, categoriaFilter]);

  // Projetos disponíveis pro seletor do detalhe da Solicitação. A RLS de `projetos` já filtra
  // quem pode ver — quem não pode recebe lista vazia e o campo some na prática.
  useEffect(() => {
    supabase.from('projetos').select('*').order('nome')
      .then(({ data }) => { if (data) setProjetos(data as Projeto[]); });
  }, []);

  // Role Access Checks

  // Role Access Checks
  const isVendedor = profile?.role === 'vendedor';
  const isParceiro = profile?.role === 'parceiro';
  const canSeeAll = ['adm', 'gerente'].includes(profile?.role || '');
  // #6518: direcionar e assumir valem pela equipe que atende cada solicitação (permissoesSolicitacao.ts).
  // Direcionar: adm/gerente da equipe dela; assumir: qualquer pessoa da equipe.
  const canDirecionar = (t: Tarefa) => podeDirecionarSolicitacao(t, profile);
  const canTrocarResponsavel = (t: Tarefa) => podeTrocarResponsavel(t, profile);
  // Botão "Auditoria": o painel é o das reuniões de Tecnologia e Marketing (as equipes novas ficam de fora dele),
  // então só quem é dessa equipe — e adm/gerente — vê o botão.
  const podeVerAuditoria = equipesDoPerfil(profile).some(e => e.id === 'tecnologia_marketing') || profile?.role === 'adm' || profile?.role === 'gerente';
  // #6358: resolver a dependência direto no selo "Bloqueada" — quem triagem, o responsável ou quem pediu.
  // O histórico é gravado pelo banco (trg_tarefas_historico_dependencia); a aba "Depende de" tem "Voltar a depender".
  const podeResolverDependencia = (t: Tarefa) =>
    profile?.role === 'adm' || canDirecionar(t) || t.responsavel_id === user?.id || t.solicitante_id === user?.id;
  const resolverDependenciaRapido = async (t: Tarefa) => {
    const patch = { bloqueada_por_pessoa_id: null, bloqueada_por_texto: null };
    setTarefas(prev => prev.map(x => x.id === t.id ? { ...x, ...patch } : x));
    const { error } = await supabase.from('tarefas').update(patch).eq('id', t.id);
    if (error) {
      setTarefas(prev => prev.map(x => x.id === t.id ? { ...x, bloqueada_por_pessoa_id: t.bloqueada_por_pessoa_id, bloqueada_por_texto: t.bloqueada_por_texto } : x));
      addToast('Não foi possível resolver a dependência.', 'error');
      return;
    }
    addToast('Dependência resolvida. Para recolocar, abra a solicitação e use "Voltar a depender".', 'success');
  };

  // Modal States
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Tarefa | null>(null);
  // Tarefa do tipo visita → reaproveita o painel "Concluir Visita" das Rotas
  const [visitaTarefa, setVisitaTarefa] = useState<Tarefa | null>(null);
  const [leadModalId, setLeadModalId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState<'info' | 'comments'>('info');

  // Data for Selects
  const [users, setUsers] = useState<Profile[]>([]);





  // Action Modals & Dropdown
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [taskToAction, setTaskToAction] = useState<Tarefa | null>(null);
  const [newResponsibleId, setNewResponsibleId] = useState('');
  const [newDueDate, setNewDueDate] = useState('');
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Cronômetro (Iniciar/Pausar/Finalizar) — só 1 ativo por pessoa no sistema todo
  const [openTimeEntry, setOpenTimeEntry] = useState<TarefaTimeEntry | null>(null);
  const [timerBusy, setTimerBusy] = useState(false);
  const [timerTick, setTimerTick] = useState(0); // força re-render a cada segundo pra o tempo decorrido andar
  const [taskTimeEntries, setTaskTimeEntries] = useState<TarefaTimeEntry[]>([]);
  const [activityEvents, setActivityEvents] = useState<TarefaStatusEvent[]>([]);

  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Details/Comments State
  const [comments, setComments] = useState<ComentarioTarefa[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentMentions, setCommentMentions] = useState<Set<string>>(new Set()); // Track mentions
  const [sendingComment, setSendingComment] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [deleteConfirmTaskId, setDeleteConfirmTaskId] = useState<string | null>(null);

  // Editing State
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [editingDesc, setEditingDesc] = useState(false);
  const [editDescValue, setEditDescValue] = useState('');
  const [descPreviewMode, setDescPreviewMode] = useState<'edit' | 'preview'>('edit');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentValue, setEditCommentValue] = useState('');
  const [newChecklistText, setNewChecklistText] = useState('');
  const [newSubtarefaTitulo, setNewSubtarefaTitulo] = useState('');
  const [editingChecklistId, setEditingChecklistId] = useState<string | null>(null);
  const [editChecklistValue, setEditChecklistValue] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const isCreatorOrAdmin = selectedTask && (
    selectedTask.solicitante_id === user?.id ||
    selectedTask.responsavel_id === user?.id ||
    ['adm', 'gerente', 'gerente_parcerias'].includes(profile?.role || '')
  );

  const startEditTitle = () => {
    if (!selectedTask || !isCreatorOrAdmin) return;
    setEditTitleValue(selectedTask.titulo);
    setEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!selectedTask || !editTitleValue.trim()) return;
    setSavingEdit(true);
    try {
      await api.tarefas.update(selectedTask.id, { titulo: editTitleValue.trim() });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'editar_titulo_tarefa', origem: 'acao' });
      const updatedTarefas = tarefas.map(t => t.id === selectedTask.id ? { ...t, titulo: editTitleValue.trim() } : t);
      setTarefas(updatedTarefas);
      setSelectedTask({ ...selectedTask, titulo: editTitleValue.trim() });
      setEditingTitle(false);
    } catch (error) {
      addToast('Erro ao salvar título.', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const startEditDesc = () => {
    if (!selectedTask || !isCreatorOrAdmin) return;
    setEditDescValue(selectedTask.descricao || '');
    setEditingDesc(true);
  };

  const handleSaveDesc = async () => {
    if (!selectedTask) return;
    setSavingEdit(true);
    try {
      await api.tarefas.update(selectedTask.id, { descricao: editDescValue.trim() });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'editar_descricao_tarefa', origem: 'acao' });
      const updatedTarefas = tarefas.map(t => t.id === selectedTask.id ? { ...t, descricao: editDescValue.trim() } : t);
      setTarefas(updatedTarefas);
      setSelectedTask({ ...selectedTask, descricao: editDescValue.trim() });
      setEditingDesc(false);
    } catch (error) {
      addToast('Erro ao salvar descrição.', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  // Subtarefa na tela Tarefas (#6518): mesma mecânica da Central de Solicitações (parent_id),
  // um nível só. A filha herda responsável, prioridade e prazo da mãe (projeto não: ver types.ts) — dá pra mudar
  // depois abrindo a própria subtarefa.
  const handleAddSubtarefa = async () => {
    if (!selectedTask || !newSubtarefaTitulo.trim() || selectedTask.parent_id) return;
    setSavingEdit(true);
    try {
      const { data, error } = await supabase.from('tarefas').insert([{
        titulo: newSubtarefaTitulo.trim(),
        descricao: '',
        responsavel_id: selectedTask.responsavel_id,
        solicitante_id: user?.id,
        prioridade: selectedTask.prioridade,
        vencimento: selectedTask.vencimento || null,
        status: 'a_iniciar' as TarefaStatus,
        categoria: 'tarefa',
        tipo: null,
        parent_id: selectedTask.id,
        checklists: [],
        created_at: new Date().toISOString(),
      }]).select().single();
      if (error) throw error;
      setTarefas(prev => [data as Tarefa, ...prev]);
      setNewSubtarefaTitulo('');
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'criar_subtarefa', origem: 'acao' });
    } catch (error: any) {
      console.error('Erro ao criar subtarefa:', error);
      addToast('Não foi possível criar a subtarefa. Tente de novo.', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddChecklist = async () => {
    if (!selectedTask || !newChecklistText.trim()) return;
    const newItem = { id: Date.now().toString(), text: newChecklistText.trim(), completed: false };
    const updatedChecklists = [...(selectedTask.checklists || []), newItem];
    setSavingEdit(true);
    try {
      await api.tarefas.update(selectedTask.id, { checklists: updatedChecklists });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'adicionar_item_checklist', origem: 'acao' });
      const updatedTarefas = tarefas.map(t => t.id === selectedTask.id ? { ...t, checklists: updatedChecklists } : t);
      setTarefas(updatedTarefas);
      setSelectedTask({ ...selectedTask, checklists: updatedChecklists });
      setNewChecklistText('');
    } catch (error) {
      addToast('Erro ao adicionar checklist.', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSaveChecklistEdit = async (itemId: string) => {
    if (!selectedTask || !editChecklistValue.trim()) return;
    setSavingEdit(true);
    if (editChecklistValue.trim() === (selectedTask.checklists?.find(c => c.id === itemId)?.text || '')) {
      setEditingChecklistId(null);
      setSavingEdit(false);
      return;
    }
    const updatedChecklists = (selectedTask.checklists || []).map(c =>
      c.id === itemId ? { ...c, text: editChecklistValue.trim() } : c
    );
    try {
      await api.tarefas.update(selectedTask.id, { checklists: updatedChecklists });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'editar_item_checklist', origem: 'acao' });
      const updatedTarefas = tarefas.map(t => t.id === selectedTask.id ? { ...t, checklists: updatedChecklists } : t);
      setTarefas(updatedTarefas);
      setSelectedTask({ ...selectedTask, checklists: updatedChecklists });
      setEditingChecklistId(null);
    } catch (error: any) {
      addToast('Erro ao editar checklist: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleChecklist = async (itemId: string, completed: boolean) => {
    if (!selectedTask) return;
    const updatedChecklists = (selectedTask.checklists || []).map(c => c.id === itemId ? { ...c, completed } : c);
    try {
      await api.tarefas.update(selectedTask.id, { checklists: updatedChecklists });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'marcar_item_checklist', origem: 'acao' });
      const updatedTarefas = tarefas.map(t => t.id === selectedTask.id ? { ...t, checklists: updatedChecklists } : t);
      setTarefas(updatedTarefas);
      setSelectedTask({ ...selectedTask, checklists: updatedChecklists });
    } catch (error: any) {
      addToast('Erro ao atualizar checklist: ' + (error?.message || JSON.stringify(error)), 'error');
    }
  };

  const handleDeleteChecklist = async (itemId: string) => {
    if (!selectedTask) return;
    const updatedChecklists = (selectedTask.checklists || []).filter(c => c.id !== itemId);
    try {
      await api.tarefas.update(selectedTask.id, { checklists: updatedChecklists });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'excluir_item_checklist', origem: 'acao' });
      const updatedTarefas = tarefas.map(t => t.id === selectedTask.id ? { ...t, checklists: updatedChecklists } : t);
      setTarefas(updatedTarefas);
      setSelectedTask({ ...selectedTask, checklists: updatedChecklists });
    } catch (error) {
      addToast('Erro ao excluir checklist.', 'error');
    }
  };

  const startEditComment = (comment: ComentarioTarefa) => {
    if (comment.user_id !== user?.id) return;
    setEditCommentValue(comment.conteudo);
    setEditingCommentId(comment.id);
  };

  const handleSaveComment = async () => {
    if (!editingCommentId || !editCommentValue.trim()) return;
    setSavingEdit(true);
    try {
      const { error } = await supabase.from('comentarios_tarefas')
        .update({ conteudo: editCommentValue.trim() })
        .eq('id', editingCommentId);
      if (error) throw error;
      setComments(comments.map(c => c.id === editingCommentId ? { ...c, conteudo: editCommentValue.trim() } : c));
      setEditingCommentId(null);
    } catch (error) {
      addToast('Erro ao editar comentário.', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (showDetailModal && selectedTask) {
      fetchComments(selectedTask.id);
      if (selectedTask.categoria === 'solicitacao') {
        fetchTimeEntries(selectedTask.id);
        fetchActivityEvents(selectedTask.id);
      }
    }

  }, [showDetailModal, selectedTask]);

  // Cronômetro ativo do usuário logado (em qualquer solicitação) — separado do
  // fetchData principal pra não depender da ordem de resolução do AuthContext
  useEffect(() => {
    if (!user) return;
    fetchOpenTimeEntry();
  }, [user]);

  // Faz o tempo decorrido do cronômetro andar enquanto houver um aberto
  useEffect(() => {
    if (!openTimeEntry) return;
    const interval = setInterval(() => setTimerTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [openTimeEntry]);

  // Heartbeat: marca de minuto em minuto que a máquina de quem está com o cronômetro
  // aberto ainda está de pé. É o sinal que encerrar_cronometros_esquecidos() usa pra
  // cortar no minuto real em que a pessoa parou, em vez do piso genérico das 18h.
  //
  // De propósito NÃO exige interação (mouse/teclado) nem aba visível: quem usa esse
  // cronômetro é dev, que passa horas no VS Code sem tocar na aba do VigorHub — exigir
  // interação cortaria hora real dele. Fechou o notebook, o beat morre sozinho; máquina
  // de mesa que fica ligada a noite toda é problema do teto do lado do banco, não daqui.
  useEffect(() => {
    const entryId = openTimeEntry?.id;
    if (!entryId) return;

    const bater = async () => {
      // Best-effort, igual logUsageEvent: nunca trava a UI nem interrompe o cronômetro.
      // O filtro por ended_at evita ressuscitar uma entrada que o cron já fechou.
      const { error } = await supabase
        .from('tarefa_time_entries')
        .update({ last_heartbeat_at: new Date().toISOString() })
        .eq('id', entryId)
        .is('ended_at', null);
      if (error) console.warn('Heartbeat do cronômetro não registrado', error.message);
    };

    bater(); // primeiro sinal na hora, sem esperar o primeiro minuto fechar
    const interval = setInterval(bater, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [openTimeEntry?.id]);

  // Deep linking for task ID — quem aponta pra cá com ?id=N (notificação antiga, link
  // compartilhado, bookmark) abre a tarefa pelo mesmo openTaskDetails de sempre. Solicitação
  // não precisa mais de um navigate() forçado pra "cair na tela certa": ela abre pelo modal
  // global de qualquer rota (ver openTaskDetails acima).
  // Abre uma vez por id: com a lista recarregando sozinha (#6180), cada evento de um colega
  // gerava um array novo e reabria o modal que o usuário já tinha fechado.
  const idAbertoPeloLinkRef = React.useRef<string | null>(null);
  useEffect(() => {
    const taskIdParam = searchParams.get('id');
    if (!taskIdParam) { idAbertoPeloLinkRef.current = null; return; }
    if (tarefas.length === 0 || idAbertoPeloLinkRef.current === taskIdParam) return;
    const taskToOpen = tarefas.find(t => String(t.id) === taskIdParam);
    if (taskToOpen) {
      idAbertoPeloLinkRef.current = taskIdParam;
      openTaskDetails(taskToOpen);
    }
  }, [searchParams, tarefas]);

  // Set default filter for Vendedor
  // Só na tela de Tarefas: na Central a visibilidade já é a de solicitacaoVisivel, e filtrar pelo próprio id
  // esconderia do vendedor do Comercial Interno a solicitação que ele acabou de abrir (nasce sem dono) e a fila da equipe.
  useEffect(() => {
    if (categoriaFilter === 'tarefa' && (isVendedor || profile?.role === 'vendedor_parceiro' || profile?.role === 'motoboy') && user) {
      setSelectedResponsibles([user.id]);
    }
  }, [isVendedor, profile?.role, user, categoriaFilter]);

  // Ao entrar em Financeiro, Faturamento ou Comercial o filtro de responsável abre em "Todos" (#6557).
  useEffect(() => {
    if (categoriaFilter === 'solicitacao' && equipeFiltro !== 'tecnologia_marketing') setSelectedResponsibles([]);
  }, [categoriaFilter, equipeFiltro]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tarefasData, usersData] = await Promise.all([
        api.tarefas.list(),
        api.auth.listProfiles()
      ]);
      setTarefas(tarefasData);
      setUsers(usersData);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  // Lista se atualiza sozinha quando alguém cria ou altera uma tarefa/solicitação (#6180).
  // Recarga silenciosa: sem spinner e sem fechar o que estiver aberto.
  const recarregarSilenciosoRef = React.useRef<() => void>(() => {});
  const recarregarSilencioso = async () => {
    try {
      setTarefas(await api.tarefas.list());
    } catch (error) {
      console.warn('Atualização automática das tarefas falhou:', error);
    }
  };
  recarregarSilenciosoRef.current = recarregarSilencioso;
  // A tabela também guarda visitas de Rotas/Prospecção, que mudam muito mais: só recarrega
  // quando o evento é da categoria desta tela (DELETE não traz a categoria, então recarrega).
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const channel = supabase
      .channel(`tarefas-view-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tarefas' }, (payload: any) => {
        const cat = payload.new?.categoria ?? payload.old?.categoria;
        if (cat && cat !== categoriaFilter) return;
        if (t) clearTimeout(t);
        t = setTimeout(() => { recarregarSilenciosoRef.current(); }, 1500);
      })
      .subscribe();
    return () => { if (t) clearTimeout(t); supabase.removeChannel(channel); };
  }, [categoriaFilter]);

  useEffect(() => {
    const t = setTimeout(() => setBuscaDigitada(buscaTexto), 300);
    return () => clearTimeout(t);
  }, [buscaTexto]);

  useEffect(() => {
    const busca = interpretarBusca(buscaDigitada);
    if (busca.tipo === 'vazia' || categoriaFilter !== 'solicitacao') {
      setBuscaComentarios(new Set()); setBuscaUcNumeros([]); setBuscando(false);
      return;
    }
    let vivo = true;
    setBuscando(true);
    (async () => {
      try {
        let q = supabase.from('comentarios_tarefas').select('tarefa_id').limit(5000);
        if (busca.tipo === 'uc') {
          // O outro número (antigo ou novo) da mesma UC também vale.
          const { data: ucs } = await supabase.from('unidades_consumidoras')
            .select('numero_uc, outro_numero_uc')
            .or(`numero_uc_norm.eq.${busca.numero},outro_numero_uc_norm.eq.${busca.numero}`)
            .limit(20);
          const numeros = [...new Set([busca.numero, ...(ucs ?? []).flatMap((u: any) => [u.numero_uc, u.outro_numero_uc])
            .map(numeroUcNormalizado).filter(n => n.length >= 4)])];
          if (!vivo) return;
          setBuscaUcNumeros(numeros);
          q = q.or(numeros.map(n => `conteudo.imatch.${regexNumeroUc(n)}`).join(','));
        } else {
          setBuscaUcNumeros([]);
          q = q.ilike('conteudo', `%${buscaDigitada.trim()}%`);
        }
        const { data } = await q;
        if (vivo) setBuscaComentarios(new Set((data ?? []).map((c: any) => String(c.tarefa_id))));
      } catch {
        if (vivo) setBuscaComentarios(new Set());
      } finally {
        if (vivo) setBuscando(false);
      }
    })();
    return () => { vivo = false; };
  }, [buscaDigitada, categoriaFilter]);

  const fetchComments = async (taskId: string) => {
    const { data, error } = await supabase
      .from('comentarios_tarefas')
      .select('*, user:profiles(*)')
      .eq('tarefa_id', taskId) // Ensure DB allows casting or id matches type
      .order('created_at', { ascending: true });

    if (!error && data) {
      setComments((data as any[]).map(ocultarSeApagado) as any);
    }
  };

  const fetchTimeEntries = async (taskId: string) => {
    const { data, error } = await supabase
      .from('tarefa_time_entries')
      .select('*, user:profiles(*)')
      .eq('tarefa_id', taskId)
      .order('started_at', { ascending: false });

    if (!error && data) {
      setTaskTimeEntries(data as any);
    }
  };

  const fetchActivityEvents = async (taskId: string) => {
    const { data, error } = await supabase
      .from('tarefa_status_events')
      .select('*, user:profiles(*)')
      .eq('tarefa_id', taskId)
      .order('created_at', { ascending: true });

    if (!error && data) setActivityEvents(data as any);
  };

  const fetchOpenTimeEntry = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('tarefa_time_entries')
      .select('*')
      .eq('user_id', user.id)
      .is('ended_at', null)
      .maybeSingle();

    if (!error) setOpenTimeEntry(data as any);
  };

  const handleStatusChange = async (taskId: string, newStatus: TarefaStatus) => {
    // Optimistic Update
    const previousTarefas = [...tarefas];
    const updateData: Partial<Tarefa> = { status: newStatus };

    if (newStatus === 'concluido') {
      updateData.data_conclusao = new Date().toISOString();
    } else {
      updateData.data_conclusao = null; // Clear completion date if moved out of concluded
    }

    setTarefas(prev => prev.map(t => t.id === taskId ? { ...t, ...updateData } : t));

    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask({ ...selectedTask, ...updateData } as Tarefa);
    }

    setUpdatingStatus(true);
    try {
      const { error } = await supabase.from('tarefas').update(updateData).eq('id', taskId);

      if (!error) {
        logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'mudar_status_tarefa', origem: 'acao' });
        await logAction(user?.id, 'UPDATE_TAREFA_STATUS', {
          tarefa_id: taskId,
          old_status: previousTarefas.find(t => t.id === taskId)?.status,
          new_status: newStatus
        });
      }

      if (error) {
        // Revert on error
        setTarefas(previousTarefas);
        if (selectedTask && selectedTask.id === taskId) {
          setSelectedTask(previousTarefas.find(t => t.id === taskId) || null);
        }
        throw error;
      }
    } catch (error) {
      console.error(error);
      addToast('Erro ao atualizar status', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Espelha solicitacao_status no status genérico, para que "ocultar concluídas", calendário, KPI etc. continuem funcionando sem mudanças
  const mapSolicitacaoStatusToStatus = (s: SolicitacaoStatus): TarefaStatus => {
    if (s === 'novo') return 'a_iniciar';
    if (s === 'concluido') return 'concluido';
    if (s === 'rejeitado') return 'cancelado';
    return 'em_andamento'; // em_analise | em_desenvolvimento | em_teste
  };

  const handleSolicitacaoStatusChange = async (taskId: string, newStatus: SolicitacaoStatus) => {
    const previousTarefas = [...tarefas];
    const mirroredStatus = mapSolicitacaoStatusToStatus(newStatus);
    const updateData: Partial<Tarefa> = {
      solicitacao_status: newStatus,
      status: mirroredStatus,
      data_conclusao: newStatus === 'concluido' ? new Date().toISOString() : null,
    };

    setTarefas(prev => prev.map(t => t.id === taskId ? { ...t, ...updateData } : t));

    if (selectedTask && selectedTask.id === taskId) {
      setSelectedTask({ ...selectedTask, ...updateData } as Tarefa);
    }

    setUpdatingStatus(true);
    try {
      const { error } = await supabase.from('tarefas').update(updateData).eq('id', taskId);

      if (!error) {
        const oldStatus = previousTarefas.find(t => t.id === taskId)?.solicitacao_status;
        logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'mudar_status_tarefa', origem: 'acao' });
        await logAction(user?.id, 'UPDATE_TAREFA_STATUS', {
          tarefa_id: taskId,
          old_status: oldStatus,
          new_status: newStatus
        });
        await logTarefaStatusEvent({ tarefaId: taskId, userId: user?.id, fromStatus: oldStatus, toStatus: newStatus });
      }

      if (error) {
        setTarefas(previousTarefas);
        if (selectedTask && selectedTask.id === taskId) {
          setSelectedTask(previousTarefas.find(t => t.id === taskId) || null);
        }
        throw error;
      }
    } catch (error) {
      console.error(error);
      addToast('Erro ao atualizar status', 'error');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Cronômetro — Iniciar avança o status pra "Em Desenvolvimento" se ainda estiver em Novo/Em Análise
  const handleStartTimer = async (task: Tarefa) => {
    if (!user) return;
    if (openTimeEntry) {
      const runningTask = tarefas.find(t => String(t.id) === String(openTimeEntry.tarefa_id));
      addToast(`Você já tem um cronômetro rodando em "${runningTask?.titulo || 'outra tarefa'}". Pause ou finalize antes de iniciar outro.`, 'error');
      return;
    }
    setTimerBusy(true);
    try {
      const { data, error } = await supabase
        .from('tarefa_time_entries')
        // last_heartbeat_at fica de fora daqui de propósito: quem carimba a primeira batida
        // é o efeito de heartbeat, que roda na hora. Um UPDATE que falha é inofensivo
        // (best-effort), um INSERT que falha impediria de iniciar o cronômetro — isso
        // desacopla o deploy do front da aplicação da migration.
        .insert({ tarefa_id: task.id, user_id: user.id })
        .select()
        .single();
      if (error) throw error;

      setOpenTimeEntry(data as any);
      setTaskTimeEntries(prev => selectedTask?.id === task.id ? [data as any, ...prev] : prev);

      const status = (task.solicitacao_status || 'novo') as SolicitacaoStatus;
      if (status === 'novo' || status === 'em_analise') {
        await handleSolicitacaoStatusChange(task.id, 'em_desenvolvimento');
      }
      addToast('Cronômetro iniciado.', 'success');
    } catch (error: any) {
      addToast('Erro ao iniciar cronômetro: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setTimerBusy(false);
    }
  };

  // Fecha o entry aberto (usado tanto por Pausar quanto por Finalizar) — só para o relógio, não mexe em status
  const closeOpenTimeEntry = async () => {
    if (!openTimeEntry) return null;
    const endedAt = new Date();
    const durationSeconds = Math.max(0, Math.round((endedAt.getTime() - new Date(openTimeEntry.started_at).getTime()) / 1000));
    const { error } = await supabase
      .from('tarefa_time_entries')
      .update({ ended_at: endedAt.toISOString(), duration_seconds: durationSeconds })
      .eq('id', openTimeEntry.id);
    if (error) throw error;

    const closedEntry = { ...openTimeEntry, ended_at: endedAt.toISOString(), duration_seconds: durationSeconds };
    setTaskTimeEntries(prev => prev.map(e => e.id === closedEntry.id ? { ...e, ...closedEntry } : e));
    setOpenTimeEntry(null);
    return closedEntry;
  };

  const handlePauseTimer = async () => {
    setTimerBusy(true);
    try {
      await closeOpenTimeEntry();
      addToast('Cronômetro pausado.', 'success');
    } catch (error: any) {
      addToast('Erro ao pausar cronômetro: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setTimerBusy(false);
    }
  };

  // Finalizar só para o relógio; a escolha do próximo status usa o select de Status já existente no detalhe
  const handleFinishTimer = async (task: Tarefa) => {
    setTimerBusy(true);
    try {
      await closeOpenTimeEntry();
      openTaskDetails(task);
      setDetailTab('info');
      addToast('Cronômetro finalizado. Escolha o próximo status da solicitação.', 'success');
    } catch (error: any) {
      addToast('Erro ao finalizar cronômetro: ' + (error?.message || JSON.stringify(error)), 'error');
    } finally {
      setTimerBusy(false);
    }
  };

  const formatDuration = (totalSeconds: number) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = Math.floor(totalSeconds % 60);
    if (h > 0) return `${h}h ${m}min`;
    if (m > 0) return `${m}min ${s}s`;
    return `${s}s`;
  };

  // Tempo digitado manualmente pelo responsável (sem ter rodado o cronômetro) — mesma tabela,
  // com started_at/ended_at sintetizados na data informada só pra entrar corretamente nos
  // agrupamentos por período do dashboard de Auditoria; is_manual=true distingue do rastreado de verdade.
  const handleAddManualTime = async (task: Tarefa, minutes: number, date: string) => {
    if (!user || minutes <= 0) return;
    try {
      const reference = new Date(`${date}T12:00:00`).toISOString();
      const durationSeconds = Math.round(minutes * 60);
      const { data, error } = await supabase
        .from('tarefa_time_entries')
        .insert({ tarefa_id: task.id, user_id: user.id, started_at: reference, ended_at: reference, duration_seconds: durationSeconds, is_manual: true })
        .select('*, user:profiles(*)')
        .single();
      if (error) throw error;

      setTaskTimeEntries(prev => [data as any, ...prev].sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()));
      addToast('Tempo adicionado.', 'success');
    } catch (error: any) {
      addToast('Erro ao adicionar tempo: ' + (error?.message || JSON.stringify(error)), 'error');
      throw error;
    }
  };

  // Corrige a duração de uma entrada já fechada (manual ou rastreada pelo cronômetro) — não mexe
  // em started_at/ended_at, só no valor contabilizado.
  const handleEditTimeEntry = async (entryId: number, minutes: number) => {
    if (minutes <= 0) return;
    try {
      const durationSeconds = Math.round(minutes * 60);
      const { error } = await supabase.from('tarefa_time_entries').update({ duration_seconds: durationSeconds }).eq('id', entryId);
      if (error) throw error;

      setTaskTimeEntries(prev => prev.map(e => e.id === entryId ? { ...e, duration_seconds: durationSeconds } : e));
      addToast('Tempo atualizado.', 'success');
    } catch (error: any) {
      addToast('Erro ao editar tempo: ' + (error?.message || JSON.stringify(error)), 'error');
      throw error;
    }
  };

  // Segundos decorridos do cronômetro aberto — depende de timerTick só pra re-renderizar a cada segundo
  const getOpenTimerElapsedSeconds = () => {
    if (!openTimeEntry) return 0;
    void timerTick;
    return Math.max(0, Math.round((Date.now() - new Date(openTimeEntry.started_at).getTime()) / 1000));
  };

  // Ações rápidas de cronômetro no dropdown de cada card (List/Tabela/Kanban) — só pro responsável da solicitação
  const timerQuickActions = (tarefa: Tarefa) => {
    if (tarefa.categoria !== 'solicitacao' || tarefa.responsavel_id !== user?.id) return null;
    const isRunningHere = !!openTimeEntry && String(openTimeEntry.tarefa_id) === String(tarefa.id);
    if (isRunningHere) {
      return (
        <>
          <button onClick={(e) => { e.stopPropagation(); handlePauseTimer(); setOpenDropdownId(null); }} disabled={timerBusy} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
            <Pause size={14} /> Pausar cronômetro
          </button>
          <button onClick={(e) => { e.stopPropagation(); handleFinishTimer(tarefa); setOpenDropdownId(null); }} disabled={timerBusy} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
            <Square size={14} /> Finalizar cronômetro
          </button>
        </>
      );
    }
    return (
      <button onClick={(e) => { e.stopPropagation(); handleStartTimer(tarefa); setOpenDropdownId(null); }} disabled={timerBusy} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
        <Play size={14} /> Iniciar cronômetro
      </button>
    );
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId) return;

    if (categoriaFilter === 'solicitacao') {
      // Concluir pede "o que foi feito" e a dificuldade (#6354): abre a solicitação em vez de concluir no arrasto.
      if (destination.droppableId === 'concluido') {
        const alvo = tarefas.find(t => String(t.id) === String(draggableId));
        if (alvo) openTaskDetails(alvo);
        addToast('Para concluir, use "Concluir tarefa" e informe o que foi feito e a dificuldade.', 'info');
        return;
      }
      handleSolicitacaoStatusChange(draggableId, destination.droppableId as SolicitacaoStatus);
      return;
    }

    // Visita só conclui pelo painel "Concluir Visita" (status + observação obrigatórios)
    const task = tarefas.find(t => String(t.id) === String(draggableId));
    if (destination.droppableId === 'concluido' && isVisitTask(task)) {
      setVisitaTarefa(task!);
      return;
    }
    handleStatusChange(draggableId, destination.droppableId as TarefaStatus);
  };

  const handleSendComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTask) return;

    setSendingComment(true);
    try {
      const { data, error } = await supabase.from('comentarios_tarefas').insert([{
        tarefa_id: selectedTask.id,
        user_id: user?.id,
        conteudo: newComment
      }]).select('*, user:profiles(*)').single();

      if (!error) {
        await logAction(user?.id, 'CREATE_COMENTARIO_TAREFA', {
          tarefa_id: selectedTask.id,
          comentario_id: data.id
        });
        logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'comentar_tarefa', origem: 'acao' });
      }

      if (error) throw error;

      setComments(prev => [...prev, data as any]);

      // Handle Notifications for Mentions
      if (commentMentions.size > 0) {
        const notificationsToInsert = Array.from(commentMentions).map(mentionedUserId => ({
          user_id: mentionedUserId,
          type: 'mention',
          title: 'Você foi marcado em um comentário',
          content: `${profile?.full_name} mencionou você na tarefa "${selectedTask.titulo}"`,
          link: `/tarefas?id=${selectedTask.id}`,
          read: false
        }));

        await supabase.from('notifications').insert(notificationsToInsert);
        setCommentMentions(new Set()); // Clear
      }

      setNewComment('');
    } catch (error: any) {
      addToast('Erro ao enviar comentário: ' + error.message, 'error');
    } finally {
      setSendingComment(false);
    }
  };

  const handleDeleteTask = (taskId: string) => {
    setDeleteConfirmTaskId(taskId);
  };

  const confirmDeleteTask = async () => {
    if (!deleteConfirmTaskId) return;
    try {
      await api.tarefas.delete(deleteConfirmTaskId);
      setTarefas(prev => prev.filter(t => t.id !== deleteConfirmTaskId));
      if (selectedTask?.id === deleteConfirmTaskId) setShowDetailModal(false);

      await logAction(user?.id, 'DELETE_TAREFA', { tarefa_id: deleteConfirmTaskId });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'excluir_tarefa', origem: 'acao' });
    } catch (error) {
      console.error(error);
      addToast('Erro ao excluir tarefa', 'error');
    } finally {
      setDeleteConfirmTaskId(null);
    }
  };

  const handleTransferTask = async () => {
    if (!taskToAction || !newResponsibleId) return;
    try {
      // Solicitação: carimba a data em que essa pessoa pegou a demanda — vira o início da barra no Gantt.
      // Vencimento é opcional aqui — é o momento em que quem distribui a demanda define o prazo (Solicitação nunca pede isso na criação).
      const isSolicitacao = taskToAction.categoria === 'solicitacao';
      const updateData: Partial<Tarefa> = isSolicitacao
        ? { responsavel_id: newResponsibleId, data_inicio: new Date().toISOString().slice(0, 10), ...(newDueDate ? { vencimento: newDueDate } : {}) }
        : { responsavel_id: newResponsibleId };

      await api.tarefas.update(taskToAction.id, updateData);
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'transferir_tarefa', origem: 'acao' });
      setTarefas(prev => prev.map(t => t.id === taskToAction.id ? { ...t, ...updateData } : t));
      setShowTransferModal(false);

      // Verify user preference before sending
      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('task_assigned')
        .eq('user_id', newResponsibleId)
        .single();

      if (!prefs || prefs.task_assigned !== false) {
        await supabase.from('notifications').insert([{
          user_id: newResponsibleId,
          type: 'task_assigned',
          title: 'Tarefa transferida',
          content: `${profile?.full_name || 'Alguém'} transferiu a tarefa "${taskToAction.titulo}" para você.`,
          link: `/tarefas?id=${taskToAction.id}`,
          read: false
        }]);
      }

      setTaskToAction(null);
      setNewResponsibleId('');
      setNewDueDate('');
    } catch (error: any) {
      addToast('Erro ao transferir: ' + (error?.message || JSON.stringify(error)), 'error');
    }
  };

  const handleRemoveAnexo = async (url: string) => {
    if (!selectedTask) return;
    try {
      // Quem tira da lista é o banco. Filtrar a lista da TELA e gravar por cima
      // ressuscitava anexo já removido por outra pessoa e apagava o que ela
      // tivesse acabado de pôr — ver src/lib/anexosTarefa.ts (solicitação 4906).
      const listaAtualizada = await removerAnexoDaTarefa(selectedTask.id, url);
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'remover_anexo_tarefa', origem: 'acao' });
      setTarefas(prev => prev.map(t => t.id === selectedTask.id ? { ...t, anexos_url: listaAtualizada } : t));
      setSelectedTask(prev => prev ? { ...prev, anexos_url: listaAtualizada } : prev);
    } catch (error: any) {
      addToast(error?.message || 'Erro ao remover anexo.', 'error', 8000);
    }
  };

  const handleRescheduleTask = async () => {
    if (!taskToAction || !newDueDate) return;
    try {
      await api.tarefas.update(taskToAction.id, { vencimento: newDueDate });
      logUsageEvent({ userId: user?.id, modulo: 'tarefas', funcionalidade: usoFuncionalidade, acao: 'reagendar_tarefa', origem: 'acao' });
      setTarefas(prev => prev.map(t => t.id === taskToAction.id ? { ...t, vencimento: newDueDate } : t));
      // Defensivo: se o detalhe da mesma solicitação estiver aberto por trás (não é o caminho
      // normal — o modal tem seu próprio DatePicker inline — mas o dropdown do card continua
      // acessível), mantém o selectedTask em dia também.
      if (selectedTask && selectedTask.id === taskToAction.id) {
        setSelectedTask(prev => prev ? { ...prev, vencimento: newDueDate } : prev);
      }
      setShowRescheduleModal(false);
      setTaskToAction(null);
      setNewDueDate('');
    } catch (error) {
      addToast('Erro ao reagendar', 'error');
    }
  };

  const toggleDropdown = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setOpenDropdownId(openDropdownId === taskId ? null : taskId);
  };

  // Tarefa com `tipo` preenchido é uma visita comercial (TaskModal: tipo '' = tarefa comum)
  const isVisitTask = (t?: Tarefa | null) => !!(t && t.tipo);

  const openTaskDetails = (task: Tarefa) => {
    // Visita → abre o painel "Concluir Visita" (mesmo das Rotas), sem trocar de página
    if (isVisitTask(task)) {
      setVisitaTarefa(task);
      return;
    }
    // Solicitação sempre abre pelo modal global (ver src/lib/abrirSolicitacao.ts e
    // views/Tarefas/SolicitacaoModalGlobal.tsx) — inclusive quando o clique vem daqui dentro
    // da própria Central de Solicitações. É o único lugar do sistema que ainda renderiza esse
    // modal, então nunca diverge de quem abre por Projetos, Atividade Recente ou notificação.
    if (task.categoria === 'solicitacao') {
      abrirSolicitacao(task.id);
      return;
    }
    setNewSubtarefaTitulo('');
    setSelectedTask(task);
    setShowDetailModal(true);
  };

  // Mantém o quadro em dia quando uma solicitação muda pelo modal global — aberto daqui mesmo
  // (openTaskDetails acima) ou de qualquer outra tela (Projetos, Atividade Recente, notificação).
  // Ver os três eventos em views/Tarefas/useSolicitacaoActions.ts.
  useEffect(() => {
    const aoAtualizar = (e: Event) => {
      const { id, patch } = (e as CustomEvent<{ id: string; patch: Partial<Tarefa> }>).detail;
      setTarefas(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
    };
    const aoCriar = (e: Event) => {
      const { subtarefa } = (e as CustomEvent<{ subtarefa: Tarefa }>).detail;
      setTarefas(prev => [subtarefa, ...prev]);
    };
    const aoExcluir = (e: Event) => {
      const { id } = (e as CustomEvent<{ id: string }>).detail;
      setTarefas(prev => prev.filter(t => t.id !== id));
    };
    window.addEventListener(SOLICITACAO_ATUALIZADA_EVENTO, aoAtualizar);
    window.addEventListener(SOLICITACAO_CRIADA_EVENTO, aoCriar);
    window.addEventListener(SOLICITACAO_EXCLUIDA_EVENTO, aoExcluir);
    return () => {
      window.removeEventListener(SOLICITACAO_ATUALIZADA_EVENTO, aoAtualizar);
      window.removeEventListener(SOLICITACAO_CRIADA_EVENTO, aoCriar);
      window.removeEventListener(SOLICITACAO_EXCLUIDA_EVENTO, aoExcluir);
    };
  }, []);

  const paradaFromTarefa = (t: Tarefa): VisitaParadaInfo => ({
    id: String(t.id),
    titulo: t.negociacao?.titulo || t.titulo,
    checkin_em: null,
    cidade: t.negociacao?.cidade ?? null,
    bairro: t.negociacao?.bairro ?? null,
    segmento: t.negociacao?.segmento ?? null,
    telefone: t.negociacao?.whatsapp ?? null,
    kwh: t.negociacao?.consumo_medio_kwh ?? null,
    modalidade: normalizarModalidade(t.modalidade),
    negociacaoId: t.negociacao_id ?? null,
    // Habilita os chips de resultado quando é visita de pós-venda.
    tipo: t.tipo ?? null,
  });

  const handleConcluirVisitaTarefa = async (_parada: VisitaParadaInfo, dados: VisitaConclusao) => {
    if (!visitaTarefa) return;
    try {
      await concluirVisitaTarefa(visitaTarefa, dados, user?.id);
      addToast(dados.proximaVisitaData ? 'Visita concluída e próxima agendada! ✅' : 'Visita concluída! ✅', 'success');
      setVisitaTarefa(null);
      await fetchData();
    } catch (err) {
      console.error('Erro ao concluir visita:', err);
      addToast('Erro ao concluir a visita.', 'error');
    }
  };

  // Helpers
  const priorityColor = (p: string) => {
    switch (p) {
      case 'urgencia': return 'text-white bg-rose-600';
      case 'alta': return 'text-error bg-error/10';
      case 'media': return 'text-warning bg-warning/10';
      case 'baixa': return 'text-success bg-success/10';
      default: return 'text-gray-400';
    }
  };

  const statusBadge = (s: string) => {
    switch (s) {
      case 'a_iniciar': return 'bg-gray-100 text-gray-500';
      case 'em_andamento': return 'bg-info/10 text-info';
      case 'aguardando': return 'bg-warning/10 text-warning';
      case 'concluido': return 'bg-success/10 text-success';
      case 'cancelado': return 'bg-error/10 text-error';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const solicitacaoStatusLabels: Record<SolicitacaoStatus, string> = {
    novo: 'Novo', em_analise: 'Em Análise', em_desenvolvimento: 'Em Desenvolvimento',
    em_teste: 'Em Teste', concluido: 'Concluído', rejeitado: 'Rejeitado'
  };

  const solicitacaoStatusBadge = (s: string) => {
    switch (s) {
      case 'novo': return 'bg-gray-100 text-gray-500';
      case 'em_analise': return 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400';
      case 'em_desenvolvimento': return 'bg-info/10 text-info';
      case 'em_teste': return 'bg-warning/10 text-warning';
      case 'concluido': return 'bg-success/10 text-success';
      case 'rejeitado': return 'bg-error/10 text-error';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  // Rótulo/badge de status exibido, correto tanto para Tarefa quanto para Solicitação
  const getDisplayStatus = (t: Tarefa) => {
    if (t.categoria === 'solicitacao') {
      const s = (t.solicitacao_status || 'novo') as SolicitacaoStatus;
      return { label: rotuloEtapa(s, t.departamento_solicitacao), badgeClass: solicitacaoStatusBadge(s) };
    }
    return { label: t.status.replace('_', ' '), badgeClass: statusBadge(t.status) };
  };

  // Progresso de subtarefas de uma solicitação "mãe" (x concluídas / y no total) — badge no card
  const subtaskStats = (parentId: string | number) => {
    const children = tarefas.filter(t => t.parent_id != null && String(t.parent_id) === String(parentId));
    // Filha de Tarefa conclui pelo status; filha de Solicitação, pelo solicitacao_status
    const done = children.filter(t => t.categoria === 'solicitacao' ? (t.solicitacao_status || 'novo') === 'concluido' : t.status === 'concluido').length;
    return { total: children.length, done };
  };

  const priorityWeight = { 'urgencia': 4, 'alta': 3, 'media': 2, 'baixa': 1 };

  const sortedTarefas = [...tarefas].sort((a, b) => {
    // "Últimas concluídas": mais recém-concluída primeiro. Quem ainda não foi concluído
    // não some, vai para o fim da lista — senão a ordenação viraria um filtro escondido.
    if (sortOrder === 'concluidas') {
      const fimA = a.data_conclusao ? new Date(a.data_conclusao).getTime() : 0;
      const fimB = b.data_conclusao ? new Date(b.data_conclusao).getTime() : 0;
      if (fimA !== fimB) return fimB - fimA;
      if (fimA === 0) return new Date(a.vencimento || 0).getTime() - new Date(b.vencimento || 0).getTime();
      return 0;
    }
    if (sortOrder === 'priority') {
      const weightA = priorityWeight[a.prioridade as keyof typeof priorityWeight] || 0;
      const weightB = priorityWeight[b.prioridade as keyof typeof priorityWeight] || 0;
      if (weightA !== weightB) return weightB - weightA;
    }
    return new Date(a.vencimento || 0).getTime() - new Date(b.vencimento || 0).getTime();
  });

  const getResponsibleName = (id?: string | null) => {
    if (!id) return 'Não atribuído';
    const u = users.find(user => user.id === id);
    return u ? u.full_name : 'Desconhecido';
  };

  const getDueDateBadgeStyle = (dateStr: string) => {
    if (!dateStr) return 'text-gray-500';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Fix: Handle YYYY-MM-DD strings explicitly for comparison
    let dueDate: Date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      dueDate = new Date(year, month - 1, day);
    } else {
      dueDate = new Date(dateStr);
    }
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));


    if (diffDays < 0) return 'text-red-500 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/30 px-2 py-0.5 rounded-full'; // Overdue
    if (diffDays === 0) return 'text-orange-500 bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-900/30 px-2 py-0.5 rounded-full'; // Today
    if (diffDays <= 2) return 'text-yellow-600 dark:text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-900/30 px-2 py-0.5 rounded-full'; // Nearing
    return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-2 py-0.5 rounded-full'; // Future
  };

  // "Sem responsável" só existe no filtro de Solicitações, mas a seleção é persistida numa chave
  // compartilhada com a tela de Tarefas — ignora o sentinela lá pra não virar filtro invisível travado.
  const effectiveResponsibles = categoriaFilter === 'solicitacao'
    ? selectedResponsibles
    : selectedResponsibles.filter(r => r !== SEM_RESPONSAVEL);

  // Tarefa de quem saiu da empresa (inativo ou excluído) fica arquivada, mesmo com
  // "Todos os responsáveis" — ver src/lib/tarefaArquivada.ts.
  const tarefaArquivada = React.useMemo(() => criarChecagemTarefaArquivada(users), [users]);

  // Regra de quem vê o quê na Central: solicitacaoVisivel em solicitacaoTipos.ts (#6518).
  const solicitacaoVisivelAqui = (t: Tarefa) => solicitacaoVisivel(t, profile, user?.id);

  const buscaAtual = categoriaFilter === 'solicitacao' ? interpretarBusca(buscaDigitada) : { tipo: 'vazia' as const };
  const casaBusca = (t: Tarefa): boolean => {
    if (buscaAtual.tipo === 'vazia') return true;
    if (buscaComentarios.has(String(t.id))) return true;
    if (buscaAtual.tipo === 'uc') {
      const numeros = buscaUcNumeros.length ? buscaUcNumeros : [buscaAtual.numero];
      return textoCitaUc(t.titulo, numeros) || textoCitaUc(t.descricao, numeros);
    }
    return String(t.id) === buscaDigitada.trim().replace(/^#/, '')
      || textoContem(t.titulo, buscaAtual.termo) || textoContem(t.descricao, buscaAtual.termo);
  };

  const filteredTarefas = sortedTarefas.filter(t => {
    const tCategoria: TarefaCategoria = t.categoria === 'solicitacao' ? 'solicitacao' : 'tarefa';
    if (tCategoria !== categoriaFilter) return false;
    if (tarefaArquivada(t)) return false;

    // Pesquisando: tudo que a pessoa pode ver, de todas as equipes e situações (inclusive
    // concluídas e rejeitadas); os demais filtros da tela ficam de lado.
    if (buscaAtual.tipo !== 'vazia') return solicitacaoVisivelAqui(t) && casaBusca(t);

    if (!showCompletedTasks && t.status === 'concluido') return false;

    if (categoriaFilter === 'solicitacao' && !solicitacaoVisivelAqui(t)) return false;
    // Solicitação antiga sem departamento é da fila de Tecnologia e Marketing, como em permissoesSolicitacao.ts;
    // sem isso ela não apareceria em nenhuma aba.
    if (categoriaFilter === 'solicitacao'
      && equipeDoDepartamento(t.departamento_solicitacao || 'Tecnologia')?.id !== equipeFiltro) return false;

    // Role Restriction Enforcement — Tarefa comum (Solicitação: ver solicitacaoVisivel)
    if (categoriaFilter === 'tarefa' && (isVendedor || profile?.role === 'vendedor_parceiro' || profile?.role === 'motoboy')) {
      if (t.responsavel_id !== user?.id && t.solicitante_id !== user?.id) return false;
    } else if (categoriaFilter === 'tarefa' && isParceiro) {
      const responsavel = users.find(u => u.id === t.responsavel_id);
      const managedResponsavel = t.responsavel_id === user?.id || responsavel?.parceiro_id === user?.id;
      const solicitante = users.find(u => u.id === t.solicitante_id);
      const managedSolicitante = t.solicitante_id === user?.id || solicitante?.parceiro_id === user?.id;
      if (!managedResponsavel && !managedSolicitante) return false;
    }

    if (categoriaFilter === 'tarefa' && profile?.role === 'gerente_parcerias') {
      const responsible = users.find(u => u.id === t.responsavel_id);
      if (responsible?.role !== 'parceiro' && responsible?.role !== 'vendedor_parceiro' && t.responsavel_id !== user?.id && t.solicitante_id !== user?.id) return false;
    }

    if (categoriaFilter === 'tarefa' && !showParceiros && (profile?.role === 'gerente' || profile?.role === 'adm')) {
      const responsible = users.find(u => u.id === t.responsavel_id);
      if (responsible?.role === 'parceiro' || responsible?.role === 'vendedor_parceiro') return false;
    }

    // Tarefa de visita (tipo preenchido) sai da lista quando o toggle "Visitas" está desligado
    if (categoriaFilter === 'tarefa' && !showVisitas && isVisitTask(t)) return false;

    // Complex Filters
    if (complexFilters.titulo && !t.titulo.toLowerCase().includes(complexFilters.titulo.toLowerCase())) return false;
    if (complexFilters.status.length > 0 && !complexFilters.status.includes(t.status)) return false;
    if (complexFilters.prioridade !== 'all' && t.prioridade !== complexFilters.prioridade) return false;
    if (complexFilters.responsavelId.length > 0 && !complexFilters.responsavelId.includes(t.responsavel_id || '')) return false;
    // Quem abriu o chamado. Separado do responsável de propósito: na Central de Solicitações
    // quem pede quase nunca é quem executa.
    if (complexFilters.solicitanteId.length > 0 && !complexFilters.solicitanteId.includes(t.solicitante_id || '')) return false;
    if (complexFilters.projetoId.length > 0 && !complexFilters.projetoId.includes(String(t.projeto_id) || '')) return false;
    // Só na fila de Solicitações — complexFilters é compartilhado com a aba Tarefas (mesma chave
    // de localStorage), onde tipo_solicitacao é sempre null e o filtro zeraria a lista inteira.
    if (categoriaFilter === 'solicitacao' && complexFilters.tipoSolicitacao.length > 0
      && !complexFilters.tipoSolicitacao.includes(t.tipo_solicitacao || '')) return false;

    // Date Range Filter
    if (complexFilters.vencimentoInicio) {
      if (!t.vencimento || new Date(t.vencimento) < new Date(complexFilters.vencimentoInicio)) return false;
    }
    if (complexFilters.vencimentoFim) {
      if (!t.vencimento || new Date(t.vencimento) > new Date(complexFilters.vencimentoFim)) return false;
    }

    // Quick Filters (Dropdowns) — "Sem responsável" é combinável com as demais opções
    if (effectiveResponsibles.length > 0) {
      const incluiSemResponsavel = effectiveResponsibles.includes(SEM_RESPONSAVEL);
      const allowedIds = effectiveResponsibles
        .filter(r => r !== SEM_RESPONSAVEL)
        .map(r => r === 'me' ? (user?.id || '') : r);
      const match = t.responsavel_id ? allowedIds.includes(t.responsavel_id) : incluiSemResponsavel;
      if (!match) return false;
    }

    // Backlog — solicitações sem responsável ainda (fila), sem status novo dedicado: é o mesmo "novo" de sempre, só filtrado explicitamente
    if (categoriaFilter === 'solicitacao' && showBacklogOnly && t.responsavel_id) return false;

    return true;
  });

  // Subtarefas ficam de fora de Lista/Tabela/Kanban por padrão (senão o board fica poluído com filhas
  // ao lado das mães) — controlado pelo toggle "Mostrar subtarefas". O Gantt usa filteredTarefas direto
  // (sem esse corte), porque lá subtarefas são exatamente o tipo de alocação por pessoa que ele existe pra mostrar.
  // Vale para as duas telas: na Tarefas as subtarefas passaram a existir com a #6518.
  const filteredTarefasForBoard = !showSubtasks && buscaAtual.tipo === 'vazia'
    ? filteredTarefas.filter(t => !t.parent_id)
    : filteredTarefas;

  // Lista e Tabela: com "Mostrar subtarefas" ligado, cada subtarefa vem logo abaixo da mãe,
  // recuada (ver subtarefasAgrupadas.ts). O Kanban não agrupa — lá a coluna é o status —, só marca.
  const linhasListaTabela = showSubtasks
    ? agruparSubtarefas(filteredTarefasForBoard)
    : filteredTarefasForBoard.map(tarefa => ({ tarefa, nivel: 0 as const, orfa: false }));

  // Título da solicitação mãe, para o selo "Subtarefa de #N · título".
  const tituloDaMae = (t: Tarefa): string | null =>
    t.parent_id == null ? null : tarefas.find(x => String(x.id) === String(t.parent_id))?.titulo || null;

  // Subtarefa de Tarefa comum (#6518): a mãe abre no painel de Tarefas. O selo, por padrão, abre o modal de
  // Solicitação, que mostraria uma Tarefa comum com campos que não são dela.
  const abrirMaeDeTarefa = (t: Tarefa): (() => void) | undefined => t.categoria === 'solicitacao' ? undefined : () => {
    const mae = tarefas.find(x => String(x.id) === String(t.parent_id));
    if (mae) openTaskDetails(mae);
  };

  // Contador de cada aba: solicitações principais abertas que a pessoa vê (sem subtarefas, concluídas e rejeitadas).
  const abertasPorEquipe: Record<string, number> = {};
  if (categoriaFilter === 'solicitacao') {
    for (const t of tarefas) {
      if (t.categoria !== 'solicitacao' || t.parent_id || tarefaArquivada(t)) continue;
      if (['concluido', 'rejeitado'].includes(t.solicitacao_status || 'novo') || !solicitacaoVisivelAqui(t)) continue;
      const id = equipeDoDepartamento(t.departamento_solicitacao || 'Tecnologia')?.id;
      if (id) abertasPorEquipe[id] = (abertasPorEquipe[id] || 0) + 1;
    }
  }

  // Estado vazio. Na Central a lista pode ficar vazia por motivos que o usuário não enxerga (regra de quem vê,
  // equipe sem fila, pessoa sem equipe no cadastro) — o texto precisa explicar qual deles é.
  const equipeFiltroLabel = EQUIPES_SOLICITACAO.find(e => e.id === equipeFiltro)?.label;
  const semEquipeNoCadastro = categoriaFilter === 'solicitacao' && !veTodasAsSolicitacoes(profile?.role) && equipesDoPerfil(profile).length === 0;
  const textoVazio = categoriaFilter !== 'solicitacao'
    ? { titulo: 'Nenhuma tarefa encontrada', dica: 'Crie uma nova tarefa para começar.' }
    : semEquipeNoCadastro
        ? { titulo: 'Nenhuma solicitação encontrada', dica: 'Seu cadastro não está em nenhuma equipe da Central, então aqui só aparece o que você abrir ou o que for direcionado a você. Peça a um gerente para conferir seu departamento.' }
        : veTodasAsSolicitacoes(profile?.role)
          ? { titulo: 'Nenhuma solicitação encontrada', dica: `Nada em ${equipeFiltroLabel} com esses filtros. Troque de aba, abra uma nova ou ajuste os filtros.` }
          : equipeFiltro !== 'tecnologia_marketing'
            ? { titulo: 'Nenhuma solicitação encontrada', dica: `Nada em ${equipeFiltroLabel} com esses filtros. Troque de aba, abra uma nova ou ajuste os filtros.` }
            : { titulo: 'Nenhuma solicitação encontrada', dica: `Em ${equipeFiltroLabel} aparece o que você abriu, o que está com você e a fila sem responsável, se a equipe for sua. Troque de aba, abra uma nova solicitação ou ajuste os filtros.` };

  const backlogCount = categoriaFilter === 'solicitacao'
    ? tarefas.filter(t => (t.categoria === 'solicitacao') && !t.responsavel_id && solicitacaoVisivelAqui(t) && (showCompletedTasks || t.solicitacao_status !== 'rejeitado')).length
    : 0;

  // Filtro "Responsável" em Solicitações só lista quem já teve pelo menos uma solicitação atribuída
  // (qualquer status, inclusive concluída/rejeitada) — evita listar departamentos inteiros (Motoboy,
  // Pós Venda etc.) que nunca têm nada a ver com esse módulo.
  const usersWithSolicitacoes = categoriaFilter === 'solicitacao'
    ? users.filter(u => tarefas.some(t => t.categoria === 'solicitacao' && t.responsavel_id != null && String(t.responsavel_id) === String(u.id)))
    : users;

  const activeFiltersCount = Object.entries(complexFilters).filter(([k, v]) => {
    // tipoSolicitacao não conta na aba Tarefas — lá ele nem é exibido nem aplicado
    if (k === 'tipoSolicitacao' && categoriaFilter !== 'solicitacao') return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== 'all' && v !== '';
  }).length;

  if (loading) return <LoadingSpinner label="Carregando tarefas..." />;

  return (
    <div className="animate-fade-in flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-120px)] relative">

      <ConfirmationModal
        isOpen={!!deleteConfirmTaskId}
        onClose={() => setDeleteConfirmTaskId(null)}
        onConfirm={confirmDeleteTask}
        title="Excluir Tarefa"
        description="Tem certeza que deseja excluir esta tarefa?"
        confirmText="Excluir"
        variant="danger"
      />

      <TasksFilterModal
        isOpen={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={setComplexFilters}
        currentFilters={complexFilters}
        users={users}
        showTipoSolicitacao={categoriaFilter === 'solicitacao'}
        projetos={projetos}
      />

      {categoriaFilter === 'solicitacao' && (
        <div className="mb-3 shrink-0 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Central de Solicitações</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Peça para qualquer equipe: Tecnologia e Marketing, Financeiro, Faturamento e Comercial.</p>
          </div>
          {openTimeEntry && (() => {
            const runningTask = tarefas.find(t => String(t.id) === String(openTimeEntry.tarefa_id));
            return (
              <div className="flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 truncate max-w-[160px]">
                  {runningTask?.titulo || 'Cronômetro ativo'}
                </span>
                <span className="text-xs font-mono font-bold text-emerald-800 dark:text-emerald-300">
                  {formatDuration(getOpenTimerElapsedSeconds())}
                </span>
                <button
                  onClick={handlePauseTimer}
                  disabled={timerBusy}
                  title="Pausar cronômetro"
                  className="p-1 rounded-full text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-800/40 transition-colors disabled:opacity-50"
                >
                  <Pause size={14} />
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {categoriaFilter === 'solicitacao' && (
        <div className="mb-3 shrink-0 flex items-end gap-1 overflow-x-auto border-b border-gray-200 dark:border-gray-700" role="tablist" aria-label="Equipe">
          {EQUIPES_SOLICITACAO.map(eq => {
            const ativa = equipeFiltro === eq.id;
            const abertas = abertasPorEquipe[eq.id] || 0;
            return (
              <button
                key={eq.id}
                role="tab"
                aria-selected={ativa}
                onClick={() => setEquipeFiltro(eq.id)}
                className={`-mb-px px-3 py-2 border-b-2 text-sm font-semibold whitespace-nowrap flex items-center gap-1.5 transition-colors shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-t ${ativa
                  ? 'border-primary text-primary dark:text-primary-light dark:border-primary-light'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
              >
                {eq.label}
                {abertas > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${ativa ? 'bg-primary/10 text-primary dark:text-primary-light' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>{abertas}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Header / Filter Bar */}
      <div className="bg-white dark:bg-gray-800 p-3 md:p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm transition-colors relative z-20 shrink-0">

        {/* Single row on desktop, stacked on mobile */}
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3 md:gap-2">

          {/* Left: Dashboard + Filters + View Toggle */}
          <div className="flex items-center flex-wrap gap-2 md:flex-shrink-0 min-w-0">

            {categoriaFilter === 'solicitacao' && (
              <div className="relative w-full sm:w-72">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" aria-hidden="true" />
                <label htmlFor="busca-solicitacoes" className="sr-only">Pesquisar solicitações</label>
                <input
                  id="busca-solicitacoes"
                  type="text"
                  value={buscaTexto}
                  onChange={e => setBuscaTexto(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') setBuscaTexto(''); }}
                  placeholder="Pesquisar no texto ou UC 123…"
                  title="Procura no título, na descrição, nos comentários e na conclusão. Comece com UC para procurar pelo número da UC."
                  className="w-full pl-9 pr-9 py-2 text-sm rounded-full border border-gray-200 bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                />
                {buscaTexto ? (
                  <button type="button" onClick={() => setBuscaTexto('')} aria-label="Limpar pesquisa"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-gray-200">
                    {buscando ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
                  </button>
                ) : null}
              </div>
            )}

            {/* Dashboard Button — não faz sentido pra Solicitações (TI/Marketing) */}
            {categoriaFilter === 'tarefa' && (
              <button
                onClick={() => setShowDashboard(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                <BarChart3 size={16} />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            )}

            {/* KPI Button — only gerente/adm, e não em Solicitações */}
            {categoriaFilter === 'tarefa' && (profile?.role === 'adm' || profile?.role === 'gerente') && (
              <button
                onClick={() => setShowKPIDashboard(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                <Target size={16} />
                <span className="hidden sm:inline">KPI</span>
              </button>
            )}

            {/* Auditoria — substitui Dashboard/KPI nessa tela; tempo gasto, urgências, lead time por etapa */}
            {categoriaFilter === 'solicitacao' && podeVerAuditoria && (
              <button
                onClick={() => setShowAuditDashboard(true)}
                className="flex items-center gap-2 px-3 md:px-4 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                <ClipboardList size={16} />
                <span className="hidden sm:inline">Auditoria</span>
              </button>
            )}

            {/* Filter Button (Opens Modal) */}
            <button
              onClick={() => setShowFilterModal(true)}
              className={`flex items-center gap-2 px-3 md:px-4 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm
                                ${activeFiltersCount > 0
                  ? 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20 dark:bg-primary/20 dark:text-primary dark:border-primary/30'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600'
                }
                            `}
            >
              <SlidersHorizontal size={16} />
              <span className="hidden sm:inline">Filtros</span>
              {activeFiltersCount > 0 && (
                <span className="bg-primary text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            {/* View Toggle */}
            <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-full transition-all ${viewMode === 'list' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Lista"
              >
                <List size={18} />
              </button>
              <button
                onClick={() => setViewMode('kanban')}
                className={`p-1.5 rounded-full transition-all ${viewMode === 'kanban' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Kanban"
              >
                <KanbanIcon size={18} />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-1.5 rounded-full transition-all ${viewMode === 'table' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Tabela"
              >
                <TableIcon size={18} />
              </button>
              <button
                onClick={() => setViewMode('calendar')}
                className={`p-1.5 rounded-full transition-all ${viewMode === 'calendar' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                title="Calendário"
              >
                <Calendar size={18} />
              </button>
              {categoriaFilter === 'solicitacao' && (
                <button
                  onClick={() => setViewMode('gantt')}
                  className={`p-1.5 rounded-full transition-all ${viewMode === 'gantt' ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                  title="Gantt"
                >
                  <SquareChartGantt size={18} />
                </button>
              )}
            </div>

            {/* Mobile Toggle Button */}
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className={`flex md:hidden items-center justify-center p-2 rounded-full transition-all ml-auto border
                                ${showMobileFilters ? 'bg-primary/10 text-primary border-primary/20' : 'bg-white text-gray-500 border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400'}
                            `}
            >
              <ChevronDown size={18} className={`transition-transform duration-300 ${showMobileFilters ? 'rotate-180' : ''}`} />
            </button>

            {/* Divider - desktop only */}
            <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
          </div>

          {/* Center: Responsible dropdowns + Toggles + Action Button - full width on mobile */}
          <div className={`${showMobileFilters ? 'flex' : 'hidden md:flex'} flex-col md:flex-row md:flex-wrap gap-2 md:gap-2 w-full md:w-auto md:flex-auto md:items-center mt-2 md:mt-0 min-w-0`}>

            {/* Responsible Selector — em Solicitações sempre aparece e lista todo mundo, sem restrição de papel */}
            {(categoriaFilter === 'solicitacao' || (!isVendedor && profile?.role !== 'vendedor_parceiro')) && (
              <div className="relative w-full md:w-auto">
                <ResponsibleFilter
                  selectedResponsibles={selectedResponsibles}
                  setSelectedResponsibles={setSelectedResponsibles}
                  profiles={categoriaFilter === 'solicitacao' ? usersWithSolicitacoes : users}
                  currentUserProfile={profile}
                  viewContext="tarefas"
                  unrestricted={categoriaFilter === 'solicitacao'}
                  allowUnassigned={categoriaFilter === 'solicitacao'}
                />
              </div>
            )}

            {/* Divider - desktop only */}
            {(categoriaFilter === 'solicitacao' || (!isVendedor && !isParceiro)) && (
              <div className="hidden md:block h-6 w-px bg-gray-200 dark:bg-gray-700"></div>
            )}

            {/* Toggle Completed - desktop inline */}
            <button
              onClick={() => setShowCompletedTasks(!showCompletedTasks)}
              className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm whitespace-nowrap ${!showCompletedTasks
                ? 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-700 dark:text-gray-500 dark:border-gray-600'
                : 'bg-white text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              title={showCompletedTasks ? "Ocultar Concluídas" : "Exibir Concluídas"}
            >
              {showCompletedTasks ? <Eye size={13} /> : <EyeOff size={13} />}
              Concluídas
            </button>

            {/* Toggle Backlog - só Solicitações: filtra pra só quem ainda não tem responsável */}
            {categoriaFilter === 'solicitacao' && (
              <button
                onClick={() => setShowBacklogOnly(!showBacklogOnly)}
                className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm whitespace-nowrap ${showBacklogOnly
                  ? 'bg-primary/10 text-primary border-primary/20 dark:bg-primary/20 dark:border-primary/30'
                  : 'bg-white text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title="Mostrar só solicitações sem responsável"
              >
                <Archive size={13} />
                Backlog {backlogCount > 0 && `(${backlogCount})`}
              </button>
            )}

            {/* Toggle Subtarefas: escondidas do board por padrão (Solicitações e, desde a #6518, Tarefas) */}
            {(
              <button
                onClick={() => setShowSubtasks(!showSubtasks)}
                aria-pressed={showSubtasks}
                className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm whitespace-nowrap ${!showSubtasks
                  ? 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-700 dark:text-gray-500 dark:border-gray-600'
                  : 'bg-white text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title={showSubtasks ? "Ocultar Subtarefas" : "Exibir Subtarefas"}
              >
                <ListTree size={13} />
                Subtarefas
              </button>
            )}

            {/* Toggle Parceiros - desktop inline (não em Solicitações) */}
            {categoriaFilter === 'tarefa' && (profile?.role === 'gerente' || profile?.role === 'adm') && (
              <button
                onClick={() => setShowParceiros(!showParceiros)}
                className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm whitespace-nowrap ${!showParceiros
                  ? 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-700 dark:text-gray-500 dark:border-gray-600'
                  : 'bg-white text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title={showParceiros ? "Ocultar Parceiros" : "Exibir Parceiros"}
              >
                {showParceiros ? <Eye size={13} /> : <EyeOff size={13} />}
                Parceiros
              </button>
            )}

            {/* Toggle Visitas - desktop inline: esconde as tarefas geradas por visita */}
            {categoriaFilter === 'tarefa' && (
              <button
                onClick={() => setShowVisitas(!showVisitas)}
                className={`hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm whitespace-nowrap ${!showVisitas
                  ? 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-700 dark:text-gray-500 dark:border-gray-600'
                  : 'bg-white text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title={showVisitas ? "Ocultar Visitas" : "Exibir Visitas"}
              >
                <MapPin size={13} />
                Visitas
              </button>
            )}

            {/* Sort Selector - pushed right on desktop */}
            <div className="relative w-full md:w-auto md:ml-auto">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className={`flex items-center gap-2 px-3 py-2.5 md:py-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full shadow-sm hover:border-gray-300 dark:hover:border-gray-500 transition-all w-full md:w-auto justify-between group
                                    ${showSortDropdown ? 'ring-2 ring-primary/10 border-primary' : ''}
                                `}
              >
                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                  <ArrowUpDown size={15} className="text-gray-400 dark:text-gray-500 group-hover:text-primary transition-colors" />
                  <span className="text-sm font-medium">
                    {SORT_LABELS[sortOrder]}
                  </span>
                </div>
                <ChevronDown size={13} className={`text-gray-400 transition-transform duration-300 ${showSortDropdown ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showSortDropdown && (
                <>
                  <div className="fixed inset-0 z-40 bg-black/30 md:bg-transparent" onClick={() => setShowSortDropdown(false)}></div>
                  <div className="fixed bottom-0 left-0 right-0 md:absolute md:bottom-auto md:top-full md:right-0 md:left-auto mt-0 md:mt-2 w-full md:w-auto md:min-w-[220px] bg-white dark:bg-gray-800 rounded-t-2xl md:rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-3 md:p-2 z-50 animate-fade-in">
                    <div className="w-10 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mb-3 md:hidden"></div>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-2 md:hidden">Ordenar por</p>
                    {SORT_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setSortOrder(opt.value);
                          // Ordenar por "últimas concluídas" com as concluídas ocultas não
                          // mostraria nada — liga o toggle junto em vez de deixar a tela vazia.
                          if (opt.value === 'concluidas') setShowCompletedTasks(true);
                          setShowSortDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all flex items-center justify-between mb-1 last:mb-0
                                                    ${sortOrder === opt.value
                            ? 'bg-primary/5 dark:bg-primary/10 text-primary font-bold'
                            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium'
                          }
                                                `}
                      >
                        {opt.label}
                        {sortOrder === opt.value && <div className="w-2 h-2 rounded-full bg-primary"></div>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Add New Task Button */}
            <div className="w-full md:w-auto">
              <button
                onClick={() => setShowNewTaskModal(true)}
                className="w-full md:w-auto flex items-center justify-center gap-2 bg-primary text-white px-3 md:px-4 py-2.5 md:py-1.5 rounded-full text-sm font-semibold hover:bg-primary/90 shadow-sm transition-colors"
              >
                <Plus size={16} /> {categoriaFilter === 'solicitacao' ? 'Nova Solicitação' : 'Nova Tarefa'}
              </button>
            </div>
          </div>

          {/* Mobile-only toggle buttons row */}
          <div className={`${showMobileFilters ? 'flex' : 'hidden'} md:hidden items-center gap-2 flex-wrap mt-2 md:mt-0`}>
            {/* Toggle Completed - mobile */}
            <button
              onClick={() => setShowCompletedTasks(!showCompletedTasks)}
              className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm ${!showCompletedTasks
                ? 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                : 'bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              title={showCompletedTasks ? "Ocultar Concluídas" : "Exibir Concluídas"}
            >
              {showCompletedTasks ? <Eye size={16} /> : <EyeOff size={16} />}
              Concluídas
            </button>

            {/* Toggle Backlog - mobile */}
            {categoriaFilter === 'solicitacao' && (
              <button
                onClick={() => setShowBacklogOnly(!showBacklogOnly)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm ${showBacklogOnly
                  ? 'bg-primary/10 text-primary border-primary/20'
                  : 'bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title="Mostrar só solicitações sem responsável"
              >
                <Archive size={16} />
                Backlog {backlogCount > 0 && `(${backlogCount})`}
              </button>
            )}

            {/* Toggle Subtarefas - mobile */}
            {(
              <button
                onClick={() => setShowSubtasks(!showSubtasks)}
                aria-pressed={showSubtasks}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm ${!showSubtasks
                  ? 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                  : 'bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title={showSubtasks ? "Ocultar Subtarefas" : "Exibir Subtarefas"}
              >
                <ListTree size={16} />
                Subtarefas
              </button>
            )}

            {/* Toggle Parceiros - mobile (não em Solicitações) */}
            {categoriaFilter === 'tarefa' && (profile?.role === 'gerente' || profile?.role === 'adm') && (
              <button
                onClick={() => setShowParceiros(!showParceiros)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm ${!showParceiros
                  ? 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                  : 'bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title={showParceiros ? "Ocultar Parceiros" : "Exibir Parceiros"}
              >
                {showParceiros ? <Eye size={16} /> : <EyeOff size={16} />}
                Parceiros
              </button>
            )}

            {/* Toggle Visitas - mobile */}
            {categoriaFilter === 'tarefa' && (
              <button
                onClick={() => setShowVisitas(!showVisitas)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold border rounded-full transition-all shadow-sm ${!showVisitas
                  ? 'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600'
                  : 'bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                title={showVisitas ? "Ocultar Visitas" : "Exibir Visitas"}
              >
                <MapPin size={16} />
                Visitas
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 min-h-0 relative w-full flex flex-col overflow-hidden mt-4 md:mt-6">
        {viewMode === 'list' && (
          <div className="h-full overflow-y-auto overflow-x-hidden custom-scrollbar pb-16 md:pb-4 pr-1 space-y-4">
            {filteredTarefasForBoard.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700">
                <CheckSquare size={48} className="mx-auto text-gray-200 dark:text-gray-600 mb-3" />
                <h3 className="text-gray-500 dark:text-gray-400 font-medium">{textoVazio.titulo}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto px-4">{textoVazio.dica}</p>
              </div>
            ) : (
              linhasListaTabela.map(({ tarefa, nivel }) => (
                <div
                  key={tarefa.id}
                  onClick={() => openTaskDetails(tarefa)}
                  className={`bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm flex items-start gap-4 hover:shadow-md transition-all group cursor-pointer ${categoriaFilter === 'solicitacao' && ehUrgenciaAberta(tarefa) ? CLASSE_URGENCIA : ''}
                    ${tarefa.parent_id != null ? SUBTAREFA_FAIXA : ''} ${nivel === 1 ? 'relative ml-6 md:ml-10 !mt-2' : ''}`}
                >
                  {/* Subtarefa logo abaixo da mãe: conector até o card de cima */}
                  {nivel === 1 && (
                    <span aria-hidden className="absolute -left-4 md:-left-6 -top-2 h-1/2 w-3 md:w-5 border-l-2 border-b-2 border-violet-200 dark:border-violet-800 rounded-bl-lg" />
                  )}
                  <div className="mt-1">
                    <div className={`w-5 h-5 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-primary transition-colors ${tarefa.status === 'concluido' ? 'bg-primary border-primary' : ''}`}>
                      {tarefa.status === 'concluido' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                    </div>
                  </div>
                  {/* min-w-0: sem ele, texto longo (ex.: título da mãe no selo) esticava o card para fora da tela no celular */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {tarefa.parent_id != null && (
                          <SeloSubtarefa parentId={tarefa.parent_id} tituloMae={tituloDaMae(tarefa)} compacto={nivel === 1} onAbrir={abrirMaeDeTarefa(tarefa)} className="mb-1.5" />
                        )}
                        {/* Line 1: Client Name */}
                        <h3 className={`font-bold text-gray-900 dark:text-white ${tarefa.status === 'concluido' ? 'line-through text-gray-400 dark:text-gray-500' : ''}`}>
                          {tarefa.negociacao ? tarefa.negociacao.titulo : tarefa.titulo}
                        </h3>
                        {/* Line 2: Activity Name */}
                        <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                          {tarefa.negociacao ? tarefa.titulo : tarefa.descricao}
                        </p>
                        {/* Line 3: Funnel Stage */}
                        {tarefa.negociacao?.stage && (
                          <div className="flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-amber-50/60 dark:bg-amber-900/20 text-[11px] font-bold text-amber-700 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/30 w-fit">
                            <Target size={11} />
                            <span>Etapa: {nomeEtapaNaTela(tarefa.negociacao.stage)}</span>
                          </div>
                        )}
                      </div>
                      <div className={`px-2 py-1 rounded-lg text-xs font-bold uppercase ${priorityColor(tarefa.prioridade)} shrink-0 ml-3`}>
                        {tarefa.prioridade}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mt-4 text-[11px] font-bold uppercase tracking-wider text-gray-500 flex-wrap">
                      <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${getDisplayStatus(tarefa).badgeClass}`}>
                        {getDisplayStatus(tarefa).label}
                      </div>
                      {tarefa.negociacao?.consumo_medio_kwh !== undefined && tarefa.negociacao.consumo_medio_kwh !== null && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800">
                          <Zap size={10} className="text-yellow-500 fill-yellow-500" />
                          {tarefa.negociacao.consumo_medio_kwh.toLocaleString('pt-BR')} kWh
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <User size={12} />
                        {getResponsibleName(tarefa.responsavel_id)}
                      </div>
                      <div className={`flex items-center gap-1 ${getDueDateBadgeStyle(tarefa.vencimento)}`}>
                        <Clock size={12} />
                        {formatDate(tarefa.vencimento)}
                      </div>
                      {categoriaFilter === 'solicitacao' && ehUrgenciaAberta(tarefa) && <SeloUrgencia />}
                      {categoriaFilter === 'solicitacao' && <PrazoBadge tarefa={tarefa} />}
                      {categoriaFilter === 'solicitacao' && <SeloVisualizado tarefa={tarefa} />}
                    </div>
                  </div>
                  {/* Action Dropdown */}
                  <div className="relative">
                    <button onClick={(e) => toggleDropdown(e, tarefa.id)} className="p-2 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                      <MoreVertical size={18} />
                    </button>
                    {openDropdownId === tarefa.id && (
                      <div className="absolute right-0 top-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-10 w-48 overflow-hidden animate-fade-in">
                        {(tarefa.categoria !== 'solicitacao' || canTrocarResponsavel(tarefa)) && (
                          <button onClick={(e) => { e.stopPropagation(); setTaskToAction(tarefa); setShowTransferModal(true); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                            <UserPlus size={14} /> {tarefa.categoria === 'solicitacao' ? 'Direcionar' : 'Transferir'}
                          </button>
                        )}
                        {timerQuickActions(tarefa)}
                        <button onClick={(e) => { e.stopPropagation(); setTaskToAction(tarefa); setShowRescheduleModal(true); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                          <Calendar size={14} /> Reagendar
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteTask(tarefa.id); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 border-t border-gray-50 dark:border-gray-700">
                          <Trash size={14} /> Excluir
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {viewMode === 'table' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex-1 flex flex-col overflow-hidden">
            <div className="overflow-x-auto overflow-y-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 font-medium border-b border-gray-200 dark:border-gray-700 sticky top-0">
                  <tr>
                    <th className="px-6 py-4">Título</th>
                    {categoriaFilter === 'solicitacao' && <th className="px-6 py-4">Tipo</th>}
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Data Solicitação</th>
                    <th className="px-6 py-4">Vencimento</th>
                    <th className="px-6 py-4">Responsável</th>
                    <th className="px-6 py-4">Anexos</th>
                    <th className="px-6 py-4 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {linhasListaTabela.map(({ tarefa, nivel }) => (
                    <tr
                      key={tarefa.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${categoriaFilter === 'solicitacao' && ehUrgenciaAberta(tarefa) ? CLASSE_URGENCIA : ''} ${nivel === 1 ? 'bg-violet-50/40 dark:bg-violet-900/10' : ''}`}
                      onClick={() => openTaskDetails(tarefa)}
                    >
                      <td className={`px-6 py-4 font-medium text-gray-900 dark:text-white max-w-xs truncate ${tarefa.parent_id != null ? SUBTAREFA_FAIXA : ''}`}>
                        <div className={`flex items-center gap-2 ${nivel === 1 ? 'pl-5' : ''}`}>
                          {nivel === 1 && <CornerDownRight size={14} className="shrink-0 -ml-5 text-violet-400" aria-hidden />}
                          <span className={`w-2 h-2 shrink-0 rounded-full ${tarefa.prioridade === 'urgencia' ? 'bg-rose-600' : tarefa.prioridade === 'alta' ? 'bg-red-500' : tarefa.prioridade === 'media' ? 'bg-yellow-500' : 'bg-green-500'}`}></span>
                          <span className="truncate" title={tarefa.titulo}>{tarefa.titulo}</span>
                          {/* Logo abaixo da mãe, a seta e a faixa já dizem de quem é; o selo
                              só aparece quando a mãe não está na tabela (filtrada ou concluída) */}
                          {tarefa.parent_id != null && nivel === 0 && (
                            <SeloSubtarefa parentId={tarefa.parent_id} tituloMae={tituloDaMae(tarefa)} compacto onAbrir={abrirMaeDeTarefa(tarefa)} className="shrink-0" />
                          )}
                          {subtaskStats(tarefa.id).total > 0 && (
                            <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 shrink-0" title="Subtarefas concluídas">
                              <ListTree size={10} /> {subtaskStats(tarefa.id).done}/{subtaskStats(tarefa.id).total}
                            </span>
                          )}
                        </div>
                      </td>
                      {categoriaFilter === 'solicitacao' && (
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {tarefa.departamento_solicitacao && (
                              <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${DEPARTAMENTO_BADGE_CLASS[tarefa.departamento_solicitacao]}`}>
                                {tarefa.departamento_solicitacao}
                              </span>
                            )}
                            {tipoSolicitacaoInfo(tarefa.tipo_solicitacao) && (
                              <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${tipoSolicitacaoInfo(tarefa.tipo_solicitacao)!.className}`} title={isTipoOutro(tarefa.tipo_solicitacao) ? tarefa.tipo_outro_detalhe || undefined : undefined}>
                                {tipoSolicitacaoInfo(tarefa.tipo_solicitacao)!.label}
                              </span>
                            )}
                            {(tarefa.bloqueada_por_pessoa_id || tarefa.bloqueada_por_texto) && (
                              <span className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400" title={[tarefa.bloqueada_por_pessoa_id ? `Aguardando ${getResponsibleName(tarefa.bloqueada_por_pessoa_id)}` : null, tarefa.bloqueada_por_texto].filter(Boolean).join(' — ')}>
                                <GitBranch size={10} /> Bloqueada
                                {podeResolverDependencia(tarefa) && (
                                  <button type="button" onClick={e => { e.stopPropagation(); resolverDependenciaRapido(tarefa); }} title="Resolver dependência" className="ml-1 px-1.5 rounded bg-white/80 dark:bg-gray-900/40 text-emerald-700 dark:text-emerald-300 normal-case hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50">
                                    Resolver
                                  </button>
                                )}
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${getDisplayStatus(tarefa).badgeClass}`}>
                          {getDisplayStatus(tarefa).label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDate(tarefa.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`flex items-center gap-2 w-fit ${getDueDateBadgeStyle(tarefa.vencimento)}`}>
                          <Calendar size={14} />
                          {formatDate(tarefa.vencimento)}
                        </span>
                        {categoriaFilter === 'solicitacao' && (
                          <div className="flex flex-wrap items-center gap-1 mt-1">
                            {ehUrgenciaAberta(tarefa) && <SeloUrgencia />}
                            <PrazoBadge tarefa={tarefa} />
                            <SeloVisualizado tarefa={tarefa} />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300">
                            {getResponsibleName(tarefa.responsavel_id).charAt(0)}
                          </div>
                          {getResponsibleName(tarefa.responsavel_id)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {tarefa.anexos_url && tarefa.anexos_url.length > 0 ? (
                          <span className="flex items-center gap-1 text-primary">
                            <Paperclip size={14} />
                            {tarefa.anexos_url.length}
                          </span>
                        ) : <span className="text-gray-300 dark:text-gray-600">-</span>}
                      </td>
                      <td className="px-6 py-4 relative">
                        <button onClick={(e) => toggleDropdown(e, tarefa.id)} className="p-2 text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                          <MoreVertical size={18} />
                        </button>
                        {openDropdownId === tarefa.id && (
                          <div className="absolute right-8 top-10 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-10 w-48 overflow-hidden animate-fade-in">
                            {(tarefa.categoria !== 'solicitacao' || canTrocarResponsavel(tarefa)) && (
                              <button onClick={(e) => { e.stopPropagation(); setTaskToAction(tarefa); setShowTransferModal(true); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                <UserPlus size={14} /> {tarefa.categoria === 'solicitacao' ? 'Direcionar' : 'Transferir'}
                              </button>
                            )}
                            {timerQuickActions(tarefa)}
                            <button onClick={(e) => { e.stopPropagation(); setTaskToAction(tarefa); setShowRescheduleModal(true); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                              <Calendar size={14} /> Reagendar
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteTask(tarefa.id); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 border-t border-gray-50 dark:border-gray-700">
                              <Trash size={14} /> Excluir
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredTarefasForBoard.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">{textoVazio.titulo}.</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto px-4 mt-1">{textoVazio.dica}</p>
                </div>
              )}
            </div>
          </div>
        )
        }

        {viewMode === 'kanban' && (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="flex gap-4 pb-4 items-start overflow-x-auto w-full h-full min-h-0 overflow-y-hidden">
              {(categoriaFilter === 'solicitacao'
                // Colunas da aba ativa (#6518): TI com o fluxo de desenvolvimento, as outras equipes por tarefa.
                ? [...etapasDoDepartamento(EQUIPES_SOLICITACAO.find(e => e.id === equipeFiltro)?.departamentos[0])]
                : ['a_iniciar', 'em_andamento', 'aguardando', 'concluido'])
                .filter(status => showCompletedTasks || (status !== 'concluido' && status !== 'rejeitado'))
                .map(status => {
                  const columnLabel = categoriaFilter === 'solicitacao'
                    ? rotuloEtapa(status, EQUIPES_SOLICITACAO.find(e => e.id === equipeFiltro)?.departamentos[0])
                    : status.replace('_', ' ');
                  const columnDot = status === 'concluido' ? 'bg-success'
                    : status === 'rejeitado' ? 'bg-red-500'
                    : (status === 'em_andamento' || status === 'em_desenvolvimento') ? 'bg-info'
                    : (status === 'aguardando' || status === 'em_teste') ? 'bg-warning'
                    : status === 'em_analise' ? 'bg-purple-400'
                    : 'bg-gray-400';
                  const columnTasks = categoriaFilter === 'solicitacao'
                    ? filteredTarefasForBoard.filter(t => (t.solicitacao_status || 'novo') === status)
                    : filteredTarefasForBoard.filter(t => t.status === status);
                  return (
                  <div
                    key={status}
                    className="w-80 bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-4 flex flex-col h-full border border-gray-200 dark:border-gray-700 shrink-0"
                  >
                    <div className="flex items-center gap-2 mb-4 px-1">
                      <div className={`w-2 h-2 rounded-full ${columnDot}`}></div>
                      <h3 className="font-bold text-gray-700 dark:text-gray-200 capitalize">{columnLabel}</h3>
                      <span className="ml-auto bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs font-bold px-2 py-0.5 rounded-full">
                        {columnTasks.length}
                      </span>
                    </div>

                    <Droppable droppableId={status}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="flex-1 space-y-3 p-1 min-h-[50px] overflow-y-auto overflow-x-hidden custom-scrollbar"
                        >
                          {columnTasks.map((tarefa, index) => (
                            <Draggable key={tarefa.id} draggableId={String(tarefa.id)} index={index}>
                              {(provided, snapshot) => {
                                const child = (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    onClick={() => openTaskDetails(tarefa)}
                                    className={`bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-colors cursor-pointer relative group
                                                                    ${categoriaFilter === 'solicitacao' && ehUrgenciaAberta(tarefa) ? CLASSE_URGENCIA : ''}
                                                                    ${snapshot.isDragging ? 'opacity-90 ring-2 ring-primary rotate-2 scale-105 z-50' : ''} ${tarefa.parent_id != null ? SUBTAREFA_FAIXA : ''}`}
                                    style={{
                                      ...provided.draggableProps.style,
                                      left: snapshot.isDragging && (provided.draggableProps.style as any)?.left ? (provided.draggableProps.style as any).left : undefined,
                                      top: snapshot.isDragging && (provided.draggableProps.style as any)?.top ? (provided.draggableProps.style as any).top : undefined,
                                      width: snapshot.isDragging ? 300 : undefined, // Optional: fix width while dragging
                                    }}
                                  >
                                    {/* Subtarefa: de qual solicitação ela é (clique abre a mãe) */}
                                    {tarefa.parent_id != null && (
                                      <SeloSubtarefa parentId={tarefa.parent_id} tituloMae={tituloDaMae(tarefa)} onAbrir={abrirMaeDeTarefa(tarefa)} className="mb-2" />
                                    )}
                                    <div className="flex justify-between items-start gap-2 mb-2">
                                      <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
                                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${priorityColor(tarefa.prioridade)}`}>{tarefa.prioridade}</span>
                                        {tarefa.departamento_solicitacao && (
                                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${DEPARTAMENTO_BADGE_CLASS[tarefa.departamento_solicitacao]}`}>
                                            {tarefa.departamento_solicitacao}
                                          </span>
                                        )}
                                        {tipoSolicitacaoInfo(tarefa.tipo_solicitacao) && (
                                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${tipoSolicitacaoInfo(tarefa.tipo_solicitacao)!.className}`} title={isTipoOutro(tarefa.tipo_solicitacao) ? tarefa.tipo_outro_detalhe || undefined : undefined}>
                                            {tipoSolicitacaoInfo(tarefa.tipo_solicitacao)!.label}
                                          </span>
                                        )}
                                        {(tarefa.bloqueada_por_pessoa_id || tarefa.bloqueada_por_texto) && (
                                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400" title={[tarefa.bloqueada_por_pessoa_id ? `Aguardando ${getResponsibleName(tarefa.bloqueada_por_pessoa_id)}` : null, tarefa.bloqueada_por_texto].filter(Boolean).join(' — ')}>
                                            <GitBranch size={10} /> Bloqueada
                                {podeResolverDependencia(tarefa) && (
                                  <button type="button" onClick={e => { e.stopPropagation(); resolverDependenciaRapido(tarefa); }} title="Resolver dependência" className="ml-1 px-1.5 rounded bg-white/80 dark:bg-gray-900/40 text-emerald-700 dark:text-emerald-300 normal-case hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50">
                                    Resolver
                                  </button>
                                )}
                                          </span>
                                        )}
                                        {subtaskStats(tarefa.id).total > 0 && (
                                          <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400" title="Subtarefas concluídas">
                                            <ListTree size={10} /> {subtaskStats(tarefa.id).done}/{subtaskStats(tarefa.id).total}
                                          </span>
                                        )}
                                        {openTimeEntry && String(openTimeEntry.tarefa_id) === String(tarefa.id) && (
                                          <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 animate-pulse" title="Cronômetro rodando">
                                            <Play size={10} />
                                          </span>
                                        )}
                                      </div>
                                      <button onClick={(e) => toggleDropdown(e, tarefa.id)} className="shrink-0 -mr-1 -mt-1 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <MoreVertical size={14} />
                                      </button>
                                      {openDropdownId === tarefa.id && (
                                        <div className="absolute right-2 top-8 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 z-10 w-48 overflow-hidden animate-fade-in">
                                          {(tarefa.categoria !== 'solicitacao' || canTrocarResponsavel(tarefa)) && (
                                            <button onClick={(e) => { e.stopPropagation(); setTaskToAction(tarefa); setShowTransferModal(true); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                              <UserPlus size={14} /> {tarefa.categoria === 'solicitacao' ? 'Direcionar' : 'Transferir'}
                                            </button>
                                          )}
                                          {timerQuickActions(tarefa)}
                                          <button onClick={(e) => { e.stopPropagation(); setTaskToAction(tarefa); setShowRescheduleModal(true); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                            <Calendar size={14} /> Reagendar
                                          </button>
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteTask(tarefa.id); setOpenDropdownId(null); }} className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2 border-t border-gray-50 dark:border-gray-700">
                                            <Trash size={14} /> Excluir
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    {/* Line 1: Client Name (from negotiation) */}
                                    <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-0.5 truncate">
                                      {tarefa.negociacao ? tarefa.negociacao.titulo : tarefa.titulo}
                                    </h4>
                                    {/* Line 2: Activity Name */}
                                    <p className="text-gray-600 dark:text-gray-300 text-xs mb-1.5 line-clamp-2 break-words">
                                      {tarefa.negociacao ? tarefa.titulo : tarefa.descricao}
                                    </p>
                                    {/* Line 3: Funnel Stage */}
                                    {tarefa.negociacao?.stage && (
                                      <div className="flex items-center gap-1 mb-2 px-1.5 py-0.5 rounded-lg bg-amber-50/60 dark:bg-amber-900/20 text-[10px] font-bold text-amber-700 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/30 w-fit">
                                        <Target size={10} />
                                        <span>Etapa: {nomeEtapaNaTela(tarefa.negociacao.stage)}</span>
                                      </div>
                                    )}
                                    {/* kWh badge */}
                                    {tarefa.negociacao?.consumo_medio_kwh !== undefined && tarefa.negociacao.consumo_medio_kwh !== null && (
                                      <div className="flex items-center gap-1 mb-2 px-1.5 py-0.5 rounded-lg bg-blue-50/50 dark:bg-blue-900/20 text-[10px] font-bold text-blue-600 dark:text-blue-400 border border-blue-100/50 dark:border-blue-800/30 w-fit">
                                        <Zap size={9} className="text-yellow-500 fill-yellow-500" />
                                        <span>{tarefa.negociacao.consumo_medio_kwh.toLocaleString('pt-BR')} kWh</span>
                                      </div>
                                    )}
                                    <div className="flex items-center justify-between gap-2 text-xs text-gray-400 mt-auto pt-2 border-t border-gray-50 dark:border-gray-700/50">
                                      <div className="flex items-center gap-2 min-w-0">
                                        {(() => {
                                          const creator = users.find(u => u.id === tarefa.responsavel_id);
                                          if (!creator) return (
                                            <>
                                              <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                ?
                                              </div>
                                              <span className="text-[11px] text-gray-400 dark:text-gray-500">Sem responsável</span>
                                            </>
                                          );

                                          const name = creator.full_name || 'Desconhecido';
                                          const initials = name.charAt(0).toUpperCase();

                                          return (
                                            <>
                                              {creator.avatar_url ? (
                                                <img src={creator.avatar_url} alt={name} className="w-5 h-5 rounded-full object-cover border border-gray-200 dark:border-gray-600" />
                                              ) : (
                                                <div className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400">
                                                  {initials}
                                                </div>
                                              )}
                                              <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 truncate max-w-[100px]">
                                                {name}
                                              </span>
                                            </>
                                          );
                                        })()}
                                      </div>
                                      <div className={`flex items-center gap-1 shrink-0 whitespace-nowrap ${getDueDateBadgeStyle(tarefa.vencimento)}`}>
                                        <Clock size={12} />
                                        {formatDate(tarefa.vencimento)}
                                      </div>
                                      {categoriaFilter === 'solicitacao' && ehUrgenciaAberta(tarefa) && <SeloUrgencia />}
                                      {categoriaFilter === 'solicitacao' && <PrazoBadge tarefa={tarefa} className="shrink-0" />}
                                      {categoriaFilter === 'solicitacao' && <SeloVisualizado tarefa={tarefa} className="shrink-0" />}
                                    </div>
                                  </div>
                                );

                                if (snapshot.isDragging) {
                                  return createPortal(child, document.body);
                                }

                                return child;
                              }}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          {columnTasks.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                              <p className="text-xs">Não há nada aqui</p>
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                  );
                })
              }
            </div>
          </DragDropContext >
        )}

        {viewMode === 'calendar' && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col h-full">
            {/* Calendar Header */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between z-20 relative shrink-0">
              <div className="flex items-center gap-4">
                {/* Navigation Group */}
                <div className="flex items-center bg-gray-50 dark:bg-gray-700 rounded-lg p-1 border border-gray-200 dark:border-gray-600">
                  <button onClick={handlePrev} className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded-md shadow-sm transition-all text-gray-600 dark:text-gray-300">
                    <ChevronLeft size={18} />
                  </button>
                  <button onClick={goToToday} className="px-3 py-1 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-600 rounded-md transition-all mx-1">
                    Hoje
                  </button>
                  <button onClick={handleNext} className="p-1 hover:bg-white dark:hover:bg-gray-600 rounded-md shadow-sm transition-all text-gray-600 dark:text-gray-300">
                    <ChevronRight size={18} />
                  </button>
                </div>

                <h2 className="text-xl font-bold text-gray-900 dark:text-white capitalize ml-2">
                  {calendarView === 'year'
                    ? currentDate.getFullYear()
                    : calendarView === 'day'
                      ? currentDate.toLocaleString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
                      : currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })
                  }
                </h2>
              </div>

              <div className="flex gap-3 relative">
                {/* View Selector Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowCalendarViewDropdown(!showCalendarViewDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors capitalize min-w-[100px] justify-between"
                  >
                    {calendarView === 'month' ? 'Mês' : calendarView === 'week' ? 'Semana' : calendarView === 'day' ? 'Dia' : 'Ano'}
                    <ChevronDown size={16} />
                  </button>

                  {showCalendarViewDropdown && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowCalendarViewDropdown(false)}></div>
                      <div className="absolute top-full right-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1 z-30 animate-fade-in">
                        {[
                          { id: 'day', label: 'Dia' },
                          { id: 'week', label: 'Semana' },
                          { id: 'month', label: 'Mês' },
                          { id: 'year', label: 'Ano' }
                        ].map(view => (
                          <button
                            key={view.id}
                            onClick={() => {
                              setCalendarView(view.id as any);
                              setShowCalendarViewDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${calendarView === view.id ? 'bg-primary/10 text-primary font-medium' : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                          >
                            {view.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Calendar Views Grid */}
            <div className="flex-1 overflow-auto custom-scrollbar relative">

              {/* MONTH VIEW & WEEK VIEW (Shared Grid Structure) */}
              {(calendarView === 'month' || calendarView === 'week') && (
                <div className="h-full flex flex-col min-w-[800px]">
                  <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
                    {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                      <div key={day} className="py-2 text-center text-xs font-semibold text-gray-500 uppercase bg-gray-50/50 dark:bg-gray-900/50">
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="flex-1 grid grid-cols-7 auto-rows-fr">
                    {(() => {
                      const year = currentDate.getFullYear();
                      const month = currentDate.getMonth();

                      let startDate: Date;
                      let totalDaysToShow: number;

                      if (calendarView === 'month') {
                        const firstDayOfMonth = new Date(year, month, 1).getDay();
                        const daysInMonth = new Date(year, month + 1, 0).getDate();
                        totalDaysToShow = firstDayOfMonth + daysInMonth <= 35 ? 35 : 42;
                        startDate = new Date(year, month, 1 - firstDayOfMonth);
                      } else {
                        // Week View
                        const dayOfWeek = currentDate.getDay(); // 0-6
                        startDate = new Date(currentDate);
                        startDate.setDate(currentDate.getDate() - dayOfWeek);
                        totalDaysToShow = 7;
                      }

                      return Array.from({ length: totalDaysToShow }).map((_, i) => {
                        const date = new Date(startDate);
                        date.setDate(startDate.getDate() + i);

                        const isCurrentMonth = date.getMonth() === month || calendarView === 'week';
                        const isToday = new Date().toDateString() === date.toDateString();
                        const dayNum = date.getDate();

                        // Find tasks
                        const dayTasks = filteredTarefas.filter(t => {
                          if (!t.vencimento) return false;
                          const taskDate = parseLocalDate(t.vencimento);
                          return taskDate.getFullYear() === date.getFullYear() &&
                            taskDate.getMonth() === date.getMonth() &&
                            taskDate.getDate() === date.getDate();
                        });

                        return (
                          <div key={i} className={`border-b border-r border-gray-100 dark:border-gray-700 p-2 min-h-[120px] hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors flex flex-col gap-1 ${!isCurrentMonth ? 'bg-gray-50/40 dark:bg-gray-900/40' : ''}`}>
                            <div className="flex justify-between items-start">
                              <span className={`text-sm font-bold w-7 h-7 flex items-center justify-center rounded-full mb-1 ${isToday ? 'bg-primary text-white' : isCurrentMonth ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-600'}`}>
                                {dayNum}
                              </span>
                              {dayTasks.length > 0 && <span className="text-[10px] text-gray-400 font-medium">{dayTasks.length}</span>}
                            </div>
                            <div className="space-y-1 overflow-y-auto custom-scrollbar max-h-[100px]">
                              {dayTasks.map(t => (
                                <div
                                  key={t.id}
                                  onClick={() => openTaskDetails(t)}
                                  className={`text-[10px] px-2 py-1.5 rounded truncate cursor-pointer font-medium hover:opacity-80 transition-opacity flex items-center gap-1.5 shadow-sm border border-transparent hover:border-black/5 dark:hover:border-white/10 ${t.status === 'concluido' ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 line-through decoration-green-500/50' :
                                    t.prioridade === 'urgencia' ? 'bg-rose-600 text-white' :
                                    t.prioridade === 'alta' ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400' :
                                      'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                    }`}
                                >
                                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.prioridade === 'urgencia' ? 'bg-white' :
                                    t.prioridade === 'alta' ? 'bg-red-500' :
                                    t.prioridade === 'media' ? 'bg-yellow-500' : 'bg-blue-500'
                                    }`} />
                                  {t.titulo}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              {/* DAY VIEW */}
              {calendarView === 'day' && (
                <div className="p-4 h-full overflow-y-auto">
                  <div className="max-w-3xl mx-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                      <h3 className="font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <Calendar size={18} className="text-primary" />
                        Tarefas do Dia
                      </h3>
                      <span className="text-sm text-gray-500 font-medium">
                        {currentDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {(() => {
                        const dayTasks = filteredTarefas.filter(t => {
                          if (!t.vencimento) return false;
                          const taskDate = parseLocalDate(t.vencimento);
                          return taskDate.getFullYear() === currentDate.getFullYear() &&
                            taskDate.getMonth() === currentDate.getMonth() &&
                            taskDate.getDate() === currentDate.getDate();
                        });

                        if (dayTasks.length === 0) {
                          return (
                            <div className="p-12 text-center text-gray-400">
                              <div className="bg-gray-50 dark:bg-gray-700/50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckSquare size={24} className="opacity-50" />
                              </div>
                              <p>Nenhuma tarefa agendada para este dia.</p>
                              <button onClick={() => setShowNewTaskModal(true)} className="mt-4 text-primary text-sm font-semibold hover:underline">
                                + Criar tarefa
                              </button>
                            </div>
                          );
                        }

                        return dayTasks.map(t => (
                          <div key={t.id} onClick={() => openTaskDetails(t)} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer flex items-center gap-4 group">
                            <div className={`p-2 rounded-lg ${t.status === 'concluido' ? 'bg-green-100 text-green-600' : 'bg-primary/10 text-primary'
                              }`}>
                              {t.status === 'concluido' ? <CheckSquare size={20} /> : <Clock size={20} />}
                            </div>
                            <div className="flex-1">
                              <h4 className={`font-semibold text-gray-900 dark:text-white ${t.status === 'concluido' ? 'line-through text-gray-500' : ''}`}>{t.titulo}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${priorityColor(t.prioridade)}`}>
                                  {t.prioridade}
                                </span>
                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                  <User size={12} /> {getResponsibleName(t.responsavel_id)}
                                </span>
                              </div>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 group-hover:text-primary transition-colors" />
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {/* YEAR VIEW */}
              {calendarView === 'year' && (
                <div className="p-6 h-full overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {Array.from({ length: 12 }).map((_, monthIndex) => {
                      const monthDate = new Date(currentDate.getFullYear(), monthIndex, 1);
                      const monthName = monthDate.toLocaleString('pt-BR', { month: 'long' });

                      // Get info for mini grid
                      const year = currentDate.getFullYear();
                      const firstDay = new Date(year, monthIndex, 1).getDay();
                      const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();

                      // Check tasks in this month
                      const tasksInMonth = filteredTarefas.filter(t => {
                        if (!t.vencimento) return false;
                        const d = parseLocalDate(t.vencimento);
                        return d.getFullYear() === year && d.getMonth() === monthIndex;
                      }).length;

                      return (
                        <button
                          key={monthIndex}
                          onClick={() => {
                            setCurrentDate(new Date(year, monthIndex, 1));
                            setCalendarView('month');
                          }}
                          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 hover:shadow-md transition-all text-left group"
                        >
                          <div className="flex justify-between items-center mb-3">
                            <h3 className="font-bold text-gray-900 dark:text-white capitalize group-hover:text-primary transition-colors">{monthName}</h3>
                            {tasksInMonth > 0 && (
                              <span className="bg-primary/10 text-primary text-[10px] px-2 py-0.5 rounded-full font-bold">
                                {tasksInMonth}
                              </span>
                            )}
                          </div>

                          {/* Mini Grid */}
                          <div className="grid grid-cols-7 gap-y-1 gap-x-0.5 pointer-events-none opacity-60">
                            {/* Headers */}
                            {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, i) => (
                              <div key={i} className="text-[8px] text-center text-gray-400 font-bold">{d}</div>
                            ))}
                            {/* Blank start */}
                            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
                            {/* Days */}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                              const day = i + 1;
                              const hasTasks = filteredTarefas.some(t => {
                                if (!t.vencimento) return false;
                                const d = parseLocalDate(t.vencimento);
                                return d.getFullYear() === year && d.getMonth() === monthIndex && d.getDate() === day;
                              });
                              const isToday = new Date().toDateString() === new Date(year, monthIndex, day).toDateString();

                              return (
                                <div key={day} className="flex justify-center items-center h-4">
                                  <div className={`w-3 h-3 rounded-full flex items-center justify-center text-[8px] ${isToday ? 'bg-primary text-white font-bold' :
                                    hasTasks ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 font-bold' : 'text-gray-500'
                                    }`}>
                                    {day}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {viewMode === 'gantt' && categoriaFilter === 'solicitacao' && (
          <SolicitacoesGantt
            tarefas={filteredTarefas}
            users={users}
            currentDate={currentDate}
            onNavigate={setCurrentDate}
            onTaskClick={openTaskDetails}
          />
        )}
      </div>

      {/* New Task Modal */}
      {
        <TaskModal
          isOpen={showNewTaskModal}
          onClose={() => setShowNewTaskModal(false)}
          onTaskCreated={(newTask) => setTarefas(prev => [newTask, ...prev])}
          initialCategoria={categoriaFilter}
        />
      }

      {/* Task Details Modal */}
      {
        showDetailModal && selectedTask && selectedTask.categoria !== 'solicitacao' && createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
            onClick={() => { setShowDetailModal(false); setSearchParams(prev => { prev.delete('id'); return prev; }, { replace: true }); }}
          >
            <div
              className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col h-[95vh] md:h-[80vh]"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start bg-gray-50 dark:bg-gray-900/50 shrink-0">
                <div className="flex-1 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${statusBadge(selectedTask.status)}`}>
                      {selectedTask.status.replace('_', ' ')}
                    </span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${priorityColor(selectedTask.prioridade)}`}>
                      {selectedTask.prioridade}
                    </span>
                  </div>
                  {editingTitle ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={editTitleValue}
                        onChange={e => setEditTitleValue(e.target.value)}
                        className="text-xl font-bold bg-white dark:bg-gray-800 border border-primary text-gray-900 dark:text-white rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                        onKeyDown={e => e.key === 'Enter' && handleSaveTitle()}
                        autoFocus
                      />
                      <button onClick={handleSaveTitle} disabled={savingEdit} className="p-1.5 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors">
                        {savingEdit ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                      </button>
                      <button onClick={() => setEditingTitle(false)} className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 group mt-1">
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedTask.titulo}</h2>
                      {isCreatorOrAdmin && (
                        <button onClick={startEditTitle} className="p-1 text-gray-400 hover:text-primary transition-all">
                          <Edit2 size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={() => { setShowDetailModal(false); setSearchParams(prev => { prev.delete('id'); return prev; }, { replace: true }); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <X size={24} />
                </button>
              </div>

              {/* Responsive Tabs (Mobile Only) */}
              <div className="flex md:hidden border-b border-gray-200 dark:border-gray-700 shrink-0">
                <button
                  onClick={() => setDetailTab('info')}
                  className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${detailTab === 'info' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'}`}
                >
                  <List size={16} /> Detalhes
                </button>
                <button
                  onClick={() => setDetailTab('comments')}
                  className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 ${detailTab === 'comments' ? 'text-primary border-b-2 border-primary' : 'text-gray-500'}`}
                >
                  <MessageSquare size={16} /> Comentários
                  {comments.length > 0 && (
                    <span className="bg-primary text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                      {comments.length}
                    </span>
                  )}
                </button>
              </div>

              <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                {/* Main Content */}
                <div className={`flex-1 overflow-y-auto p-4 md:p-6 space-y-6 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 ${detailTab !== 'info' ? 'hidden md:block' : 'block'}`}>
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <List size={16} className="text-gray-400" />
                        Descrição
                      </div>
                      {isCreatorOrAdmin && !editingDesc && (
                        <button onClick={startEditDesc} className="p-1 text-gray-400 hover:text-primary transition-colors">
                          <Edit2 size={14} />
                        </button>
                      )}
                    </h4>
                    {editingDesc ? (
                      <div className="space-y-2">
                        {/* GitHub-style tab bar */}
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          <div className="flex items-center gap-0 bg-gray-50 dark:bg-gray-900/80 border-b border-gray-200 dark:border-gray-700 px-1">
                            <button
                              type="button"
                              onClick={() => setDescPreviewMode('edit')}
                              className={`relative px-3.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors rounded-t-md
                                ${descPreviewMode === 'edit'
                                  ? 'text-gray-800 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-b-white dark:border-b-gray-800 -mb-px'
                                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-transparent'}`}
                            >
                              <Edit2 size={12} /> Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => setDescPreviewMode('preview')}
                              className={`relative px-3.5 py-2 flex items-center gap-1.5 text-xs font-medium transition-colors rounded-t-md
                                ${descPreviewMode === 'preview'
                                  ? 'text-gray-800 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-b-white dark:border-b-gray-800 -mb-px'
                                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-transparent'}`}
                            >
                              <Eye size={12} /> Preview
                            </button>
                          </div>

                          {/* Content area */}
                          {descPreviewMode === 'edit' ? (
                            <div className="p-0">
                              <MentionTextarea
                                users={users}
                                value={editDescValue}
                                onChange={e => setEditDescValue(e.target.value)}
                                onMention={() => { }}
                                className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border-0 text-xs
                                           text-gray-900 dark:text-slate-200
                                           focus:outline-none focus:ring-0 resize-y min-h-[150px]
                                           font-mono leading-relaxed rounded-b-lg"
                                placeholder="Descrição da tarefa... (suporta **markdown**)"
                              />
                            </div>
                          ) : (
                            <div className="min-h-[150px] px-4 py-3 bg-white dark:bg-gray-800 overflow-y-auto max-h-[300px] rounded-b-lg">
                              {editDescValue.trim() ? (
                                <div className="max-w-none text-sm">
                                  <TaskDescriptionMarkdown value={editDescValue} variant="compact" />
                                </div>
                              ) : (
                                <p className="text-gray-400 dark:text-gray-600 text-xs italic text-center mt-8">
                                  Nada para visualizar ainda...
                                </p>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Markdown hint */}
                        <p className="text-[10px] text-gray-400 dark:text-gray-600">
                          Suporta **negrito**, *itálico*, `código`, # títulos, listas e tabelas.
                        </p>

                        {/* Action buttons */}
                        <div className="flex justify-end gap-2">
                          <button onClick={() => { setEditingDesc(false); setDescPreviewMode('edit'); }} className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-slate-700 rounded-xl transition-colors">Cancelar</button>
                          <button onClick={handleSaveDesc} disabled={savingEdit} className="px-4 py-2 text-xs font-semibold text-white bg-primary hover:bg-primary/90 rounded-xl flex items-center gap-1 shadow-lg shadow-primary/20">
                            {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
                          </button>
                        </div>
                      </div>
                    ) : selectedTask.descricao ? (
                      <div className="max-w-none text-sm">
                        <TaskDescriptionMarkdown value={selectedTask.descricao} variant="full" />
                      </div>
                    ) : (
                      <p className="text-gray-400 dark:text-gray-500 text-sm italic">Sem descrição.</p>
                    )}
                  </div>

                  {/* Checklist Section */}
                  <div className="mt-6 border-t border-gray-100 dark:border-gray-800 pt-6">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <CheckSquare size={16} className="text-gray-400" />
                      Checklist
                    </h4>

                    <div className="space-y-2 mb-3">
                      {(selectedTask.checklists || []).map(item => (
                        <div key={item.id} className="flex items-center gap-2 group">
                          <div className="flex-1 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg text-sm flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={item.completed}
                              onChange={(e) => handleToggleChecklist(item.id, e.target.checked)}
                              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary outline-none"
                            />
                            {editingChecklistId === item.id ? (
                              <input
                                type="text"
                                value={editChecklistValue}
                                onChange={(e) => setEditChecklistValue(e.target.value)}
                                onBlur={() => handleSaveChecklistEdit(item.id)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSaveChecklistEdit(item.id)}
                                autoFocus
                                className="flex-1 bg-transparent border-none outline-none text-gray-700 dark:text-gray-200 focus:ring-0 px-1 -mx-1"
                              />
                            ) : (
                              <span
                                className={`flex-1 ${item.completed ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'} ${isCreatorOrAdmin ? 'cursor-text hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded px-1 -mx-1' : ''}`}
                                onClick={() => {
                                  if (isCreatorOrAdmin) {
                                    setEditChecklistValue(item.text);
                                    setEditingChecklistId(item.id);
                                  }
                                }}
                              >
                                {item.text}
                              </span>
                            )}
                          </div>
                          {isCreatorOrAdmin && (
                            <button
                              onClick={() => handleDeleteChecklist(item.id)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {isCreatorOrAdmin && (
                      <div className="flex flex-col sm:flex-row gap-2 mt-2">
                        <input
                          type="text"
                          value={newChecklistText}
                          onChange={(e) => setNewChecklistText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddChecklist();
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-gray-900 dark:text-gray-200"
                          placeholder="Adicionar item..."
                        />
                        <button
                          onClick={handleAddChecklist}
                          disabled={!newChecklistText.trim() || savingEdit}
                          className="px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 dark:bg-primary/20 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
                        >
                          Adicionar
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Subtarefa aberta: caminho de volta para a tarefa mãe */}
                  {selectedTask.parent_id != null && (
                    <SeloSubtarefa
                      parentId={selectedTask.parent_id}
                      tituloMae={tituloDaMae(selectedTask)}
                      onAbrir={abrirMaeDeTarefa(selectedTask)}
                    />
                  )}

                  {/* Subtarefas (#6518) — só na tarefa mãe; um nível, como na Central de Solicitações */}
                  {!selectedTask.parent_id && (() => {
                    const filhas = tarefas.filter(t => t.parent_id != null && String(t.parent_id) === String(selectedTask.id));
                    return (
                      <div className="mt-6 border-t border-gray-100 dark:border-gray-800 pt-6">
                        <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                          <ListTree size={16} className="text-gray-400" />
                          Subtarefas
                          {filhas.length > 0 && (
                            <span className="text-xs font-semibold text-gray-400">
                              {filhas.filter(f => f.status === 'concluido').length}/{filhas.length}
                            </span>
                          )}
                        </h4>
                        <div className="space-y-2 mb-3">
                          {filhas.map(sub => (
                            <div key={sub.id} className="flex items-center gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 rounded-lg text-sm">
                              <label className="flex items-center justify-center p-1.5 -m-1.5 cursor-pointer shrink-0">
                                <input
                                  type="checkbox"
                                  checked={sub.status === 'concluido'}
                                  disabled={updatingStatus}
                                  onChange={(e) => handleStatusChange(sub.id, e.target.checked ? 'concluido' : 'a_iniciar')}
                                  aria-label={`Concluir subtarefa ${sub.titulo}`}
                                  className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary outline-none disabled:opacity-50"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => openTaskDetails(sub)}
                                className={`flex-1 min-w-0 text-left truncate hover:text-primary dark:hover:text-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm ${sub.status === 'concluido' ? 'line-through text-gray-400' : 'text-gray-700 dark:text-gray-200'}`}
                              >
                                {sub.titulo}
                              </button>
                            </div>
                          ))}
                          {filhas.length === 0 && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 italic">Nenhuma subtarefa ainda.</p>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={newSubtarefaTitulo}
                            onChange={(e) => setNewSubtarefaTitulo(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubtarefa(); } }}
                            className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white dark:bg-gray-900 dark:text-gray-200"
                            placeholder="Título da subtarefa..."
                            aria-label="Título da nova subtarefa"
                          />
                          <button
                            onClick={handleAddSubtarefa}
                            disabled={!newSubtarefaTitulo.trim() || savingEdit}
                            className="px-4 py-2 bg-primary/10 text-primary hover:bg-primary/20 dark:bg-primary/20 rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors"
                          >
                            Adicionar subtarefa
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {selectedTask.negociacao && (
                    <div className="bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50">
                      <h4 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Briefcase size={14} />
                        Negociação Vinculada
                      </h4>
                      <div className="flex flex-wrap items-center gap-y-3 gap-x-6">
                        <div>
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 block mb-0.5">Título</span>
                          <span className="text-sm font-bold text-gray-700 dark:text-gray-200">{selectedTask.negociacao.titulo}</span>
                        </div>
                        {selectedTask.negociacao.consumo_medio_kwh !== undefined && (
                          <div>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 block mb-0.5">Consumo Médio</span>
                            <div className="flex items-center gap-1 text-sm font-bold text-gray-700 dark:text-gray-200">
                              <Zap size={14} className="text-yellow-500 fill-yellow-500" />
                              {selectedTask.negociacao.consumo_medio_kwh.toLocaleString('pt-BR')} kWh
                            </div>
                          </div>
                        )}
                        {selectedTask.negociacao.stage && (
                          <div>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 block mb-0.5">Etapa do Funil</span>
                            <div className="flex items-center gap-1">
                              <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wide bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                                {nomeEtapaNaTela(selectedTask.negociacao.stage)}
                              </span>
                            </div>
                          </div>
                        )}
                        {selectedTask.negociacao.status && (
                          <div>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 block mb-0.5">Status da Negociação</span>
                            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wide ${(() => {
                              switch (selectedTask.negociacao.status) {
                                case 'em_andamento': return 'bg-blue-100 text-blue-700';
                                case 'ganho': return 'bg-green-100 text-green-700';
                                case 'perdido': return 'bg-red-100 text-red-700';
                                case 'em_pausa': return 'bg-yellow-100 text-yellow-700';
                                case 'simulacao_realizada': return 'bg-purple-100 text-purple-700';
                                case 'termo_enviado': return 'bg-yellow-100 text-yellow-700';
                                case 'termo_assinado': return 'bg-teal-100 text-teal-700';
                                case 'termo_analise': return 'bg-orange-100 text-orange-700';
                                case 'termo_aprovado': return 'bg-green-100 text-green-700';
                                case 'termo_reprovado': return 'bg-red-100 text-red-700';
                                default: return 'bg-gray-100 text-gray-700';
                              }
                            })()}`}>
                              {(() => {
                                const map: Record<string, string> = {
                                  'em_andamento': 'Em andamento',
                                  'ganho': 'Ganho',
                                  'perdido': 'Perdido',
                                  'em_pausa': 'Em pausa',
                                  'simulacao_realizada': 'Simulação realizada',
                                  'termo_enviado': 'Termo enviado',
                                  'termo_assinado': 'Termo assinado',
                                  'termo_analise': 'Termo em análise',
                                  'termo_aprovado': 'Termo aprovado',
                                  'termo_reprovado': 'Termo reprovado'
                                };
                                return map[selectedTask.negociacao.status] || selectedTask.negociacao.status;
                              })()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 bg-gray-50 dark:bg-gray-900/30 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                    <div>
                      <span className="text-xs text-gray-400 block mb-1">Solicitante</span>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400 flex items-center justify-center text-xs font-bold">
                          {selectedTask.solicitante_id ? getResponsibleName(selectedTask.solicitante_id).charAt(0) : '?'}
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{selectedTask.solicitante_id ? getResponsibleName(selectedTask.solicitante_id) : 'Sistema'}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block mb-1">Responsável</span>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">
                          {getResponsibleName(selectedTask.responsavel_id).charAt(0)}
                        </div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{getResponsibleName(selectedTask.responsavel_id)}</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-400 block mb-1">Vencimento</span>
                      <div className={`flex items-center gap-2 w-fit ${getDueDateBadgeStyle(selectedTask.vencimento)}`}>
                        <Calendar size={14} />
                        <span className="text-sm font-medium">{selectedTask.vencimento ? parseLocalDate(selectedTask.vencimento).toLocaleDateString() : '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Attachments Section in Details */}
                  {selectedTask.anexos_url && selectedTask.anexos_url.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                        <Paperclip size={16} className="text-gray-400" />
                        Anexos
                      </h4>
                      <div className="flex flex-col gap-2">
                        {selectedTask.anexos_url.map((url, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-1 min-w-0 px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-primary hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center gap-2 truncate"
                            >
                              <Paperclip size={14} />
                              Anexo {i + 1}
                            </a>
                            {isCreatorOrAdmin && (
                              <button
                                type="button"
                                onClick={() => handleRemoveAnexo(url)}
                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors shrink-0"
                                title="Remover anexo"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Status</h4>
                    <select
                      value={selectedTask.status}
                      onChange={(e) => handleStatusChange(selectedTask.id, e.target.value as TarefaStatus)}
                      className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                    >
                      <option value="a_iniciar">A Iniciar</option>
                      <option value="em_andamento">Em Andamento</option>
                      <option value="aguardando">Aguardando</option>
                      <option value="concluido">Concluído</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>

                </div>

                {/* Comments Section */}
                <div className={`w-full md:w-80 bg-gray-50 dark:bg-gray-900 flex flex-col shrink-0 ${detailTab !== 'comments' ? 'hidden md:flex' : 'flex'}`}>
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <h4 className="font-bold text-gray-700 dark:text-white text-sm flex items-center gap-2">
                      <MessageSquare size={16} className="text-gray-400 dark:text-slate-400" />
                      Comentários
                    </h4>
                    <span className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-[10px] font-bold px-2 py-0.5 rounded-full">{comments.length}</span>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {comments.length === 0 ? (
                      <p className="text-center text-gray-400 text-xs py-8">Nenhum comentário ainda.</p>
                    ) : (
                      comments.map(comment => (
                        <div key={comment.id} className="group">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold ${corDoUsuario(comment.user_id).nome}`}>{comment.user?.full_name || 'Usuário'}</span>
                              <span className="text-[10px] text-gray-400 dark:text-slate-500">{new Date(comment.created_at).toLocaleString()}</span>
                            </div>
                            {comment.user_id === user?.id && editingCommentId !== comment.id && !comentarioApagado(comment) && (
                              <button onClick={() => startEditComment(comment)} className="p-0.5 text-gray-400 hover:text-primary transition-all">
                                <Edit2 size={12} />
                              </button>
                            )}
                          </div>

                          {comentarioApagado(comment) ? (
                            <div className="p-3 rounded-lg rounded-tl-none border border-dashed border-gray-200 dark:border-slate-700 text-xs italic text-gray-400 dark:text-slate-500">
                              {TEXTO_COMENTARIO_APAGADO}
                            </div>
                          ) : editingCommentId === comment.id ? (
                            <div className="bg-white dark:bg-slate-800 p-3 rounded-lg rounded-tl-none border border-primary/30 dark:border-primary/50 shadow-sm space-y-3 mt-1">
                              <MentionTextarea
                                users={users}
                                value={editCommentValue}
                                onChange={e => setEditCommentValue(e.target.value)}
                                onMention={() => { }}
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-primary/50 dark:border-primary/50 rounded-lg text-sm dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-y min-h-[80px]"
                              />
                              <div className="flex justify-end gap-2">
                                <button onClick={() => setEditingCommentId(null)} className="px-3 py-1.5 text-[11px] font-semibold text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 rounded-lg transition-colors">Cancelar</button>
                                <button onClick={handleSaveComment} disabled={savingEdit} className="px-3 py-1.5 text-[11px] font-semibold text-white bg-primary hover:bg-primary/90 rounded-lg flex items-center gap-1 shadow-md shadow-primary/20">
                                  {savingEdit ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Salvar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className={`bg-white dark:bg-slate-800/50 p-3 rounded-lg rounded-tl-none border border-gray-200 dark:border-slate-700 border-l-4 ${corDoUsuario(comment.user_id).barra} shadow-sm text-sm text-gray-600 dark:text-slate-300`}>
                              <ComentarioTexto texto={comment.conteudo} usuarios={users} />
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  <form onSubmit={handleSendComment} className="p-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                    <div className="relative">
                      <MentionTextarea
                        users={users}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onMention={(userId) => setCommentMentions(prev => new Set(prev).add(userId))}
                        placeholder="Escreva um comentário... (suporta **markdown**, @ para mencionar)"
                        className="w-full pl-4 pr-10 py-3 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-sm dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none min-h-[50px]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendComment(e);
                          }
                        }}
                      />

                      <button
                        type="submit"
                        disabled={!newComment.trim() || sendingComment}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-primary hover:bg-primary/10 rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Send size={18} />
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>, document.body
        )
      }

      {/* Transfer Modal */}
      {
        showTransferModal && (
          createPortal(
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" onClick={() => { setShowTransferModal(false); setNewDueDate(''); }}>
              <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-800 mb-4">{taskToAction?.categoria === 'solicitacao' ? 'Direcionar Solicitação' : 'Transferir Tarefa'}</h3>
                <p className="text-gray-600 mb-4 text-sm">Selecione o novo responsável para: <strong>{taskToAction?.titulo}</strong></p>
                <select
                  className={`w-full px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none focus:ring-2 focus:ring-primary/20 ${taskToAction?.categoria === 'solicitacao' ? 'mb-4' : 'mb-6'}`}
                  value={newResponsibleId}
                  onChange={e => setNewResponsibleId(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {users.filter(u => {
                    if (u.status === 'inactive') return false;

                    // Solicitação só pode ir pra alguém da equipe que a atende (#6518), não importa quem está direcionando
                    if (taskToAction?.categoria === 'solicitacao') {
                      return daEquipeDaSolicitacao(taskToAction, u) && podeEntrarNaCentral(u) && podeReceberPedido(u);
                    }

                    const userRole = profile?.role;
                    const userId = user?.id;

                    if (userRole === 'adm' || userRole === 'gerente') return true;

                    if (userRole === 'gerente_parcerias') {
                      return u.id === userId || u.role === 'parceiro' || u.role === 'vendedor_parceiro';
                    }

                    if (userRole === 'parceiro') {
                      return u.id === userId || (u.role === 'vendedor_parceiro' && u.parceiro_id === userId);
                    }

                    if (userRole === 'vendedor_parceiro') {
                      return u.id === userId;
                    }

                    return true; // Default for other roles
                  }).map(u => (
                    <option key={u.id} value={u.id}>{u.full_name}</option>
                  ))}
                </select>
                {taskToAction?.categoria === 'solicitacao' && (
                  <div className="mb-6">
                    <label className="text-xs font-medium text-gray-500 block mb-1">
                      Prazo de entrega <span className="text-gray-400 font-normal">(opcional, dá pra ajustar depois)</span>
                    </label>
                    <DatePicker value={newDueDate} onChange={setNewDueDate} placeholder="Sem prazo definido" />
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button onClick={() => { setShowTransferModal(false); setNewDueDate(''); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium">Cancelar</button>
                  <button onClick={handleTransferTask} disabled={!newResponsibleId} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50">{taskToAction?.categoria === 'solicitacao' ? 'Direcionar' : 'Transferir'}</button>
                </div>
              </div>
            </div>, document.body
          )
        )
      }

      {/* Reschedule Modal */}
      {
        showRescheduleModal && (
          createPortal(
            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" onClick={() => { setShowRescheduleModal(false); setNewDueDate(''); }}>
              <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6" onClick={e => e.stopPropagation()}>
                <h3 className="text-lg font-bold text-gray-800 mb-4">Alterar Vencimento</h3>
                <p className="text-gray-600 mb-4 text-sm">Defina a nova data para: <strong>{taskToAction?.titulo}</strong></p>
                <div className="mb-6">
                  <DatePicker value={newDueDate} onChange={setNewDueDate} placeholder="Selecionar data" />
                </div>
                <div className="flex justify-end gap-3">
                  <button onClick={() => { setShowRescheduleModal(false); setNewDueDate(''); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium">Cancelar</button>
                  <button onClick={handleRescheduleTask} disabled={!newDueDate} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50">Salvar</button>
                </div>
              </div>
            </div>, document.body
          )
        )
      }

      <ModalTasksGraphic
        isOpen={showDashboard}
        onClose={() => setShowDashboard(false)}
        selectedResponsibles={selectedResponsibles}
      />

      <TarefasKPIDashboard
        isOpen={showKPIDashboard}
        onClose={() => setShowKPIDashboard(false)}
      />

      <SolicitacoesAuditDashboard
        isOpen={showAuditDashboard}
        onClose={() => setShowAuditDashboard(false)}
      />

      {/* Painel "Concluir Visita" reaproveitado das Rotas para tarefas do tipo visita */}
      {visitaTarefa && (
        <VisitaModal
          parada={paradaFromTarefa(visitaTarefa)}
          etapa="encerrar"
          modo="comercial"
          onClose={() => setVisitaTarefa(null)}
          onIniciar={async () => { /* não há check-in numa tarefa de visita */ }}
          onEncerrar={handleConcluirVisitaTarefa}
          onAbrirLead={(id) => { setVisitaTarefa(null); setLeadModalId(id); }}
          onChangeModalidade={async (m) => {
            if (!visitaTarefa) return;
            await supabase.from('tarefas').update({ modalidade: m }).eq('id', visitaTarefa.id);
            setVisitaTarefa(prev => prev ? { ...prev, modalidade: m } : prev);
            setTarefas(prev => prev.map(t => t.id === visitaTarefa.id ? { ...t, modalidade: m } : t));
          }}
        />
      )}

      <NegotiationDetailModal
        isOpen={leadModalId !== null}
        negotiationId={leadModalId}
        onClose={() => setLeadModalId(null)}
        onUpdate={fetchData}
      />
    </div>
  );
};

export default TarefasView;
